// src/pages/profile/doctor/DoctorProfile.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopBarProfile from "@/sot/primitives/TopBarProfile.jsx";
import CoverPhoto from "@/sot/primitives/CoverPhoto.jsx";
import AvatarHeaderProfile from "@/sot/primitives/AvatarHeaderProfile.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import { loadDoctorIntroPreview } from "@/lib/doctorIntroPreview.js";
import { assertHydrationOwner } from "@/lib/hydrationIsolationTelemetry.js";
import { resolveAddressedProfileName } from "@/pages/shared/profileAddressedName.js";
import {
  readDoctorConsultancies,
  writeDoctorConsultancies,
} from "@/lib/localDoctorStorage";
import Sheet from "@/sot/primitives/Sheet.jsx";
import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import Divider from "@/components/ui/Divider.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import supabase from "@/services/supabaseClient";
import {
  fetchDoctorPublicPatientCount,
  fetchDoctorUniquePatientCount,
} from "@/lib/doctorPatientStats.js";
import { resolveInsuranceDisplay } from "@/pages/appointments/insuranceVisibility.js";
import { summarizeDoctorPaymentMethods } from "@/services/doctorPaymentPreferences.js";
import { resolveDoctorAvatarSrc } from "@/lib/doctorsDirectory.js";
import Card from "@/components/ui/Card.jsx";

// ---------------------------------------------------------------------------
// Asset paths
// ---------------------------------------------------------------------------

const ICON_PATIENTS = "/icons/doctor/diversity_4.svg";
const ICON_EXPERIENCE = "/icons/doctor/medical_services.svg";
const ICON_STAR = "/icons/doctor/star.svg";
const ICON_UPLOAD_IMAGE = "/icons/doctor/imagesmode.svg"
const ICON_TAKE_PHOTO = "/icons/doctor/photo_camera.svg"

const ICON_HOSPITAL = "/icons/doctor/hospital.svg";
const ICON_FAMILY = "/icons/doctor/family_restroom.svg";
const ICON_DATE_RANGE = "/icons/doctor/date_range.svg";
const ICON_INSURANCE = "/icons/doctor/volunteer_activism.svg";
const ICON_PAYMENT_TERMS = "/icons/doctor/credit_card.svg";

const ICON_EVISIT = "/icons/doctor/E-Visit.svg";
const ICON_AVAILABILITY = "/icons/doctor/date_range.svg";

const ICON_NAVIGATE_NEXT = "/icons/doctor/navigate_next.svg";
const ICON_DELETE_CRITICAL = "/icons/doctor/delete_critical.svg";

const ICON_VERIFIED = "/icons/doctor/verified.svg";
const ICON_PENDING_VERIFICATION = "/icons/pending-verification.svg";
const ICON_SHARE_PROFILE = "/icons/message-square-share.svg";
const ICON_SHARE_X = "/icons/X.svg";
const ICON_SHARE_FACEBOOK = "/icons/facebook.svg";
const ICON_SHARE_LINKEDIN = "/icons/linkedin.svg";
const DEFAULT_DOCTOR_COVER_IMAGE = "/icons/doctor/EditIntro_banner.svg";
const PROFILE_WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const PRACTICE_MODE_ALIASES = {
  virtual: "virtual_only",
  physical: "in_person_only",
};
const PROFILE_SECTION_ORDER_DEFAULT = [
  "languages",
  "consultancies",
  "eVisit",
  "gallery",
  "services",
];
const PROFILE_SECTION_ORDER_BY_PRACTICE_MODE = {
  virtual_only: ["languages", "eVisit", "services", "gallery", "consultancies"],
  in_person_only: ["languages", "consultancies", "services", "gallery"],
  hybrid: ["languages", "eVisit", "consultancies", "services", "gallery"],
};

// ✅ DEV keys (must exist before any localStorage reads)
const DEV_COVER_KEY_PREFIX = "afrimed:dev:doctor_cover:";
const DEV_PHOTO_KEY = "afrimed:dev:doctor_profile_photo";

// ---------------------------------------------------------------------------
// Small reusable bits
// ---------------------------------------------------------------------------

function StatChip({ icon, label, value, largeIcon = false }) {
  const showCenteredRatingStar = largeIcon && icon === ICON_STAR;

  return (
    <Card
      className="
        flex-1 min-w-[96px]
        px-4 py-2
        flex items-center justify-center
      "
    >
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center gap-1">
            <span className="whitespace-nowrap text-[15px] sm:text-label-large text-on-surface leading-tight">
              {value}
            </span>

          {showCenteredRatingStar ? (
            <span className="inline-flex h-6 w-6 items-center justify-center text-[#FEBE00]">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="h-5 w-5"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2.9L14.76 8.49L20.93 9.39L16.46 13.74L17.51 19.89L12 16.99L6.49 19.89L7.54 13.74L3.07 9.39L9.24 8.49L12 2.9Z" />
              </svg>
            </span>
          ) : icon ? (
            <span
              className={`inline-flex items-center justify-center ${
                largeIcon ? "h-6 w-6" : "h-[18px] w-[18px]"
              }`}
            >
              <img src={icon} alt="" className="h-full w-full" />
            </span>
          ) : null}
        </div>

        <div className="mt-[2px] text-label-medium text-on-surface-var">
          {label}
        </div>
      </div>
    </Card>
  );
}

function FocusChip({ label }) {
  return (
    <button
      type="button"
      className="
        inline-flex flex-col justify-center
        h-[32px] px-3
        rounded-lg
        bg-[var(--state-primary-12,#4DA3FF)]
        text-label-large text-on-surface
        whitespace-nowrap
      "
    >
      <span className="leading-[18px]">{label}</span>
    </button>
  );
}

function orderLanguageList(items) {
  const source = Array.isArray(items) ? items : String(items || "").split(",");
  return Array.from(
    new Map(
      source
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.language || item?.label || item?.name || ""
        )
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .map((item) => [item.toLowerCase(), item])
    ).values()
  );
}

function firstPublicText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function positivePublicNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

function hasPublicDoctorHeadshot(profile = {}) {
  const candidate = firstPublicText(
    profile.profile_photo_path,
    profile.profile_image_url,
    profile.profile_photo_url,
    profile.avatar_url,
  );
  if (!candidate) return false;
  return !/profile_avatar|avatar_placeholder|circular-avatar-placeholder/i.test(candidate);
}

function hasPublicDoctorServiceAndPrice(profile = {}) {
  const services = Array.isArray(profile.doctor_services) ? profile.doctor_services : [];
  if (
    services.some(
      (service) =>
        service?.is_active !== false &&
        firstPublicText(service?.name, service?.label) &&
        positivePublicNumber(service?.price),
    )
  ) {
    return true;
  }

  return (
    firstPublicText(profile.service_label, profile.primary_service_label, profile.service_name) &&
    positivePublicNumber(profile.service_price ?? profile.primary_service_price ?? profile.consultation_fee)
  );
}

function hasPublicDoctorLocationOrTelehealth(profile = {}) {
  const consultancies = Array.isArray(profile.doctor_consultancies)
    ? profile.doctor_consultancies
    : [];
  const hasConsultancyLocation = consultancies.some((consultancy) =>
    firstPublicText(
      consultancy?.facility_address,
      consultancy?.facility_city,
      consultancy?.facility_country,
      consultancy?.facility_name,
      consultancy?.address,
      consultancy?.city,
      consultancy?.country,
      consultancy?.name,
    )
  );

  return (
    hasConsultancyLocation ||
    profile.evisit_enabled === true ||
    firstPublicText(profile.evisit_availability)
  );
}

function isPublicDoctorProfileViewable(profile = {}) {
  const status = String(profile.verified_status || "").trim().toLowerCase();
  const classification = String(profile.account_classification || "external").trim().toLowerCase();
  const specialty = firstPublicText(
    profile.primary_specialty,
    profile.specialty,
    ...(Array.isArray(profile.specialties) ? profile.specialties : []),
    ...(Array.isArray(profile.specialization) ? profile.specialization : []),
  );

  return (
    classification !== "internal" &&
    classification !== "test" &&
    classification !== "smoke" &&
    profile.exclude_from_public_discovery !== true &&
    profile.public_discovery_excluded !== true &&
    (status === "verified" || profile.onboarding_completed === true) &&
    firstPublicText(profile.full_name, profile.display_name, profile.first_name, profile.last_name) &&
    specialty &&
    hasPublicDoctorHeadshot(profile) &&
    hasPublicDoctorServiceAndPrice(profile) &&
    hasPublicDoctorLocationOrTelehealth(profile)
  );
}

const FRENCH_DISPLAY_LABELS = {
  specialty: {
    "Specialty not added yet": "Spécialité non renseignée",
    "Allergy/Immunology": "Allergologie / Immunologie",
    "Allergy and Immunology": "Allergologie / Immunologie",
    Pediatric: "Pédiatrie",
    Paediatric: "Pédiatrie",
    Dermatologist: "Dermatologue",
    Dermatology: "Dermatologie",
    "General Practice": "Médecine générale",
    "general practitioner": "Médecin généraliste",
    "Preventive Medicine": "Médecine préventive",
    "Emergency Medicine": "Médecine d’urgence",
  },
  focus: {
    "Allergic Diseases": "Maladies allergiques",
    "Immune System Disorders": "Troubles du système immunitaire",
    anaphylaxis: "Anaphylaxie",
    immunoglobulin: "Immunoglobuline",
    immunotherapy: "Immunothérapie",
    immunodeficiencies: "Déficits immunitaires",
    "Food Allergy": "Allergie alimentaire",
    Asthma: "Asthme",
    Eczema: "Eczéma",
  },
  language: {
    English: "Anglais",
    French: "Français",
    Tswana: "Setswana",
    Setswana: "Setswana",
    "Zimbabwean Ndebele": "Ndebele du Zimbabwe",
    Ndebele: "Ndebele",
    Portuguese: "Portugais",
    German: "Allemand",
  },
  service: {
    "Allergy testing and management": "Tests et prise en charge des allergies",
    "Professional mole assessment": "Évaluation professionnelle des grains de beauté",
    "Food Allergy Management": "Prise en charge des allergies alimentaires",
    "Asthma and Respiratory Care": "Prise en charge de l’asthme et des troubles respiratoires",
    "Atopic Dermatitis (Eczema)": "Dermatite atopique (eczéma)",
    "Primary Immune Deficiency Diseases (PIDDs)": "Déficits immunitaires primitifs",
    "Virtual consultation": "Consultation virtuelle",
    "General consultation": "Consultation générale",
    Consultation: "Consultation",
  },
};
const SPECIALTY_DISPLAY_NORMALIZATIONS = {
  "general practitioner": "General Practitioner",
  "public health specialist": "Public Health Specialist",
};

function isFrenchLanguageCode(languageCode = "") {
  return String(languageCode || "").trim().toLowerCase().startsWith("fr");
}

function splitDisplayParts(value = "") {
  return String(value || "")
    .split(/\s*([,/]| - )\s*/)
    .filter((part) => part !== "");
}

function localizeDisplayLabel(value, languageCode, group = "specialty") {
  const rawValue = String(value || "").trim();
  if (!rawValue) return rawValue;
  const raw =
    group === "specialty"
      ? SPECIALTY_DISPLAY_NORMALIZATIONS[rawValue.toLowerCase()] || rawValue
      : rawValue;
  if (!isFrenchLanguageCode(languageCode)) return raw;

  const labels = FRENCH_DISPLAY_LABELS[group] || {};
  if (labels[raw]) return labels[raw];

  const parts = splitDisplayParts(raw);
  if (parts.length > 1) {
    return parts
      .map((part) => labels[part.trim()] || part)
      .join("")
      .replace(/\s*,\s*/g, ", ")
      .replace(/\s*\/\s*/g, " / ")
      .replace(/\s+-\s+/g, " - ");
  }

  return raw;
}

function localizeDisplayList(items, languageCode, group) {
  return (Array.isArray(items) ? items : [])
    .map((item) => localizeDisplayLabel(item, languageCode, group))
    .filter(Boolean);
}

function SectionHeader({ title, actionLabel, onAction }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-title-medium text-on-surface-var">{title}</h2>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="text-label-large text-primary"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function InfoRow56({
  icon,
  label,
  value,
  valueRight,
  dense = false,
  largeIcon = false,
}) {
  return (
    <div
      className={`
        w-full flex items-center
        ${dense ? "py-2" : "py-[10px]"}
        px-4
      `}
    >
      {icon && (
        <span className="mr-3 inline-flex h-11 w-11 items-center justify-center">
          <img
            src={icon}
            alt=""
            className={largeIcon ? "h-11 w-11" : "h-6 w-6"}
          />
        </span>
      )}

      <div className="flex-1 min-w-0">
        {label && (
          <div className="text-label-medium text-on-surface-var truncate">
            {label}
          </div>
        )}
        {value && (
          <div className="text-body-large text-on-surface truncate">
            {value}
          </div>
        )}
      </div>

      {valueRight && (
        <div className="ml-3 text-label-large text-primary whitespace-nowrap">
          {valueRight}
        </div>
      )}
    </div>
  );
}

// Facility header row – special typography
function FacilityHeaderRow({ icon, name, address }) {
  return (
    <div className="w-full flex items-center px-4 py-[10px]">
      {icon && (
        <span className="mr-3 inline-flex h-11 w-11 items-center justify-center">
          <img src={icon} alt="" className="h-11 w-11" />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-title-medium text-on-surface truncate">
          {name}
        </div>
        {address && (
          <div className="text-body-medium text-on-surface-var truncate">
            {address}
          </div>
        )}
      </div>
    </div>
  );
}

// Virtual consultation main row – special typography
function EVisitRow({ icon, label, status, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        w-full flex items-center px-4 py-[10px]
        text-left
      "
    >
      {icon && (
        <span className="mr-3 inline-flex h-11 w-11 items-center justify-center">
          <img src={icon} alt="" className="h-11 w-11" />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-title-medium text-on-surface truncate">
          {label}
        </div>
      </div>

      <div className="ml-3 inline-flex items-center gap-1">
        <span className="text-body-medium text-on-surface-var whitespace-nowrap">
          {status}
        </span>
        <img
          src={ICON_NAVIGATE_NEXT}
          alt=""
          className="h-5 w-5 text-on-surface-var"
        />
      </div>
    </button>
  );
}

function CardDivider() {
  return <Divider variant="outlineVariant" inset className="opacity-[0.35]" />;
}

function SectionDivider() {
  return <Divider variant="outlineVariant" className="my-10 -mx-4 md:mx-0" />;
}

// ---------------------------------------------------------------------------
// View-model helpers
// ---------------------------------------------------------------------------

const DOCTOR_SERVICES_KEY = "afrimed.services.doctor";
const DOCTOR_PROFILE_SERVICES_KEY = "afrimed.profile.doctor.services";
const DOCTOR_EVISIT_KEY = "afrimed_doctor_evisit_v1";
const DOCTOR_FOCUS_KEY = "afrimed:dev:doctor_focus_tags_v1";
const DOCTOR_GALLERY_BUCKET = "doctor-profile-images";

// Safe JSON helper (only ONE version of this in the file)
function safeParseJson(value) {
  if (value == null) return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toArray(maybe) {
  const val = safeParseJson(maybe) ?? maybe;

  if (Array.isArray(val)) return val;

  if (val && typeof val === "object") {
    // common shapes – try the obvious keys first
    if (Array.isArray(val.items)) return val.items;
    if (Array.isArray(val.data)) return val.data;
    if (Array.isArray(val.services)) return val.services;
    if (Array.isArray(val.images)) return val.images;
    return Object.values(val);
  }

  return [];
}

function loadFromStorage(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return safeParseJson(raw) ?? raw;
  } catch {
    return null;
  }
}

function loadScopedFromStorage(key, userId = "") {
  const uid = String(userId || "").trim();
  if (!uid) return null;
  return loadFromStorage(`${key}:${uid}`);
}

function normalizePracticeMode(value) {
  const raw = String(value || "").trim();
  const normalized = PRACTICE_MODE_ALIASES[raw] || raw;
  return Object.prototype.hasOwnProperty.call(
    PROFILE_SECTION_ORDER_BY_PRACTICE_MODE,
    normalized,
  )
    ? normalized
    : "";
}

function replaceWholeWord(text, search, replacement) {
  return String(text || "").replace(new RegExp(`\\b${search}\\b`, "g"), replacement);
}

function localizeWeekdayTokens(value, t) {
  const replacements = [
    ["Monday", t("doctor.profile.values.days.monday", "Monday")],
    ["Tuesday", t("doctor.profile.values.days.tuesday", "Tuesday")],
    ["Wednesday", t("doctor.profile.values.days.wednesday", "Wednesday")],
    ["Thursday", t("doctor.profile.values.days.thursday", "Thursday")],
    ["Friday", t("doctor.profile.values.days.friday", "Friday")],
    ["Saturday", t("doctor.profile.values.days.saturday", "Saturday")],
    ["Sunday", t("doctor.profile.values.days.sunday", "Sunday")],
    ["Mon", t("doctor.profile.values.days.mon", "Mon")],
    ["Tue", t("doctor.profile.values.days.tue", "Tue")],
    ["Wed", t("doctor.profile.values.days.wed", "Wed")],
    ["Thu", t("doctor.profile.values.days.thu", "Thu")],
    ["Fri", t("doctor.profile.values.days.fri", "Fri")],
    ["Sat", t("doctor.profile.values.days.sat", "Sat")],
    ["Sun", t("doctor.profile.values.days.sun", "Sun")],
  ];

  return replacements.reduce(
    (current, [search, replacement]) => replaceWholeWord(current, search, replacement),
    String(value || ""),
  );
}

function localizeConsultancyValue(kind, rawValue, t) {
  const value = String(rawValue || "").trim();
  if (!value) return value;
  const normalized = value.toLowerCase();

  if (kind === "patientsAge") {
    if (normalized === "children & adults" || normalized === "adults & kids") {
      return t("doctor.profile.values.patientAge.childrenAdults", "Children & Adults");
    }
    if (normalized === "adults only") {
      return t("doctor.profile.values.patientAge.adultsOnly", "Adults only");
    }
    if (normalized === "children only" || normalized === "kids only") {
      return t("doctor.profile.values.patientAge.childrenOnly", "Children only");
    }
  }

  if (kind === "availability") {
    if (normalized === "every day" || normalized === "daily") {
      return t("doctor.profile.values.availability.everyDay", "Every day");
    }

    const prefixed = value.replace(
      /^every week/i,
      t("doctor.profile.values.availability.everyWeek", "Every week"),
    );

    return localizeWeekdayTokens(prefixed, t);
  }

  if (kind === "insurancePolicy" && normalized === "accepting") {
    return t("doctor.profile.values.insurance.accepting", "Accepting");
  }

  if (kind === "insurancePolicy") {
    const clean = value.replace(/^accepts:\s*/i, "").trim();
    if (clean && clean !== value) return `${t("doctor.profile.values.insurance.accepts", "Accepts")}: ${clean}`;
    if (clean.includes(",")) return `${t("doctor.profile.values.insurance.accepts", "Accepts")}: ${clean}`;
  }

  if (kind === "paymentTerms") {
    let current = value;
    current = current.replace(
      /Debit\s*\/\s*Credit cards/gi,
      t("doctor.profile.values.paymentTerms.cards", "Debit / Credit cards"),
    );
    current = current.replace(
      /Credit cards/gi,
      t("doctor.profile.values.paymentTerms.creditCards", "Credit cards"),
    );
    current = current.replace(
      /Debit cards/gi,
      t("doctor.profile.values.paymentTerms.debitCards", "Debit cards"),
    );
    current = current.replace(
      /Bank transfers/gi,
      t("doctor.profile.values.paymentTerms.bankTransfers", "Bank transfers"),
    );
    current = current.replace(
      /Cash on service/gi,
      t("doctor.profile.values.paymentTerms.cashOnService", "Cash on service"),
    );
    return current;
  }

  return value;
}

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function countConfiguredAvailabilityDays(availabilityRaw) {
  const availability = parseJsonObject(availabilityRaw);
  const workingHours =
    availability?.workingHours && typeof availability.workingHours === "object"
      ? availability.workingHours
      : null;
  if (workingHours) {
    const enabledCount = PROFILE_WEEKDAY_KEYS.filter(
      (key) => workingHours?.[key]?.enabled === true,
    ).length;
    if (enabledCount > 0) return enabledCount;
  }

  const schedule = availability?.schedule && typeof availability.schedule === "object"
    ? availability.schedule
    : null;
  const frequency = String(schedule?.frequencyType || "").trim().toLowerCase();
  if (frequency === "daily") return 7;
  if (Array.isArray(schedule?.weeklyDays)) {
    return new Set(schedule.weeklyDays.map((day) => String(day || "").trim()).filter(Boolean)).size;
  }
  return 0;
}

function resolveAvailabilitySummary(profile, t) {
  const dayCount = countConfiguredAvailabilityDays(profile?.availability);
  const settings =
    parseJsonObject(profile?.appointment_settings) ||
    parseJsonObject(profile?.appointmentSettings);
  const maxAdvanceBooking = Number(
    settings?.maxAdvanceBooking ||
      settings?.max_advance_booking ||
      profile?.consultation_fees?.maxAdvanceBooking ||
      profile?.consultation_fees?.max_advance_booking ||
      0,
  );

  if (dayCount > 0) {
    const dayLabel =
      dayCount === 1
        ? t("doctor.profile.values.availability.regularDay", "regular day")
        : t("doctor.profile.values.availability.regularDays", "regular days");
    const aheadDays = Number.isFinite(maxAdvanceBooking) && maxAdvanceBooking > 0
      ? maxAdvanceBooking
      : 90;
    return `${dayCount} ${dayLabel} • ${aheadDays} ${t("doctor.profile.values.availability.daysAhead", "days ahead")}`;
  }

  return String(
    profile?.evisit_availability ||
      profile?.consultation_fees?.evisit_availability ||
      profile?.eVisit?.availability_text ||
      "",
  ).trim();
}

function resolvePaymentTermsSummary(profile, t) {
  const metaSource =
    profile?.payout_meta ||
    profile?.payoutMeta ||
    profile?.payment_methods ||
    profile?.paymentMethods ||
    null;
  if (!metaSource) return "";
  const summary = summarizeDoctorPaymentMethods(metaSource);
  const labels = Array.isArray(summary.labels) ? summary.labels.filter(Boolean) : [];
  if (!labels.length) return "";
  return [
    t("doctor.profile.values.paymentTerms.selfPay", "Self-pay"),
    ...labels,
  ].join(" / ");
}

function resolveEVisitPriceLabel(profile) {
  const fees = parseJsonObject(profile?.consultation_fees);
  const rawPrice =
    fees?.evisit_price ??
    fees?.e_visit_price ??
    fees?.eVisitPrice ??
    profile?.evisit_price ??
    profile?.e_visit_price ??
    null;
  const price = Number(rawPrice);
  if (!Number.isFinite(price) || price <= 0) return "";
  const currency = String(
    fees?.evisit_currency ||
      fees?.e_visit_currency ||
      fees?.currency ||
      profile?.evisit_currency ||
      profile?.currency ||
      "BWP",
  ).trim() || "BWP";
  return `${price.toFixed(2)} ${currency}`;
}

function resolveDoctorIdCandidates(...values) {
  const out = [];
  const seen = new Set();
  for (const value of values) {
    const id = String(value || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function readConsultanciesFromCandidateIds(...values) {
  const ids = resolveDoctorIdCandidates(...values);
  for (const id of ids) {
    const rows = readDoctorConsultancies(id);
    if (Array.isArray(rows) && rows.length > 0) {
      return rows;
    }
  }
  return [];
}

function pickScopedConsultancyField(previewRow, dbRow, keys = []) {
  for (const key of keys) {
    const previewValue = previewRow?.[key];
    if (typeof previewValue === "string" && previewValue.trim()) return previewValue;
    if (previewValue != null && previewValue !== "") return previewValue;
  }
  for (const key of keys) {
    const dbValue = dbRow?.[key];
    if (typeof dbValue === "string" && dbValue.trim()) return dbValue;
    if (dbValue != null && dbValue !== "") return dbValue;
  }
  return "";
}

function mergeConsultancyRowsById(dbRows = [], previewRows = []) {
  const ordered = [];
  const dbById = new Map();
  const previewById = new Map();

  (Array.isArray(dbRows) ? dbRows : []).forEach((row, index) => {
    const id = String(row?.id || `db-${index}`).trim();
    dbById.set(id, row || {});
    ordered.push(id);
  });

  (Array.isArray(previewRows) ? previewRows : []).forEach((row, index) => {
    const id = String(row?.id || `preview-${index}`).trim();
    previewById.set(id, row || {});
    if (!ordered.includes(id)) ordered.push(id);
  });

  return ordered.map((id) => {
    const dbRow = dbById.get(id) || {};
    const previewRow = previewById.get(id) || {};
    return {
      ...dbRow,
      ...previewRow,
      id,
      facility_name: pickScopedConsultancyField(previewRow, dbRow, [
        "facility_name",
        "name",
        "location_name",
      ]),
      name: pickScopedConsultancyField(previewRow, dbRow, [
        "name",
        "facility_name",
        "location_name",
      ]),
      facility_address: pickScopedConsultancyField(previewRow, dbRow, [
        "facility_address",
        "address",
        "address_line",
      ]),
      address: pickScopedConsultancyField(previewRow, dbRow, [
        "address",
        "address_line",
        "facility_address",
      ]),
      address_line: pickScopedConsultancyField(previewRow, dbRow, [
        "address_line",
        "address",
        "facility_address",
      ]),
      facility_city: pickScopedConsultancyField(previewRow, dbRow, [
        "facility_city",
        "city",
      ]),
      city: pickScopedConsultancyField(previewRow, dbRow, [
        "city",
        "facility_city",
      ]),
      facility_country: pickScopedConsultancyField(previewRow, dbRow, [
        "facility_country",
        "country",
      ]),
      country: pickScopedConsultancyField(previewRow, dbRow, [
        "country",
        "facility_country",
      ]),
      patients_age: pickScopedConsultancyField(previewRow, dbRow, [
        "patients_age",
        "patient_age_range",
      ]),
      patient_age_range: pickScopedConsultancyField(previewRow, dbRow, [
        "patient_age_range",
        "patients_age",
      ]),
      availability_text: pickScopedConsultancyField(previewRow, dbRow, [
        "availability_text",
        "availabilityText",
      ]),
      insurance_policy: pickScopedConsultancyField(previewRow, dbRow, [
        "insurance_policy",
        "insurancePolicy",
      ]),
      payment_terms: pickScopedConsultancyField(previewRow, dbRow, [
        "payment_terms",
        "payment_methods",
        "paymentTerms",
      ]),
      payment_methods: pickScopedConsultancyField(previewRow, dbRow, [
        "payment_methods",
        "payment_terms",
        "paymentTerms",
      ]),
      is_primary:
        typeof previewRow?.is_primary === "boolean"
          ? previewRow.is_primary
          : typeof dbRow?.is_primary === "boolean"
          ? dbRow.is_primary
          : false,
      sort_order:
        previewRow?.sort_order ?? dbRow?.sort_order ?? null,
      updated_at: previewRow?.updated_at || dbRow?.updated_at || null,
    };
  });
}

function normalizeGalleryIdentity(item, idx = 0) {
  const path = String(item?.path || item?.storage_path || "").trim();
  if (path) return path.replace(/^public\//, "").toLowerCase();

  const url = String(
    typeof item === "string" ? item : item?.url || item?.image_url || item?.src || "",
  ).trim();
  if (!url) return String(item?.id || `gallery-${idx}`).trim();

  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://afrimed.tech");
    return parsed.pathname
      .replace(/^\/storage\/v1\/object\/(?:public|sign)\//, "")
      .replace(/^\/+/, "")
      .toLowerCase();
  } catch {
    return url.split("?")[0].split("#")[0].toLowerCase();
  }
}

function useDoctorGalleryPreview(profile) {
  const [images, setImages] = React.useState([]);

  React.useEffect(() => {
    let alive = true;
    setImages([]);

    const ownerId = String(profile?.user_id || profile?.doctor_id || profile?.id || "").trim();
    if (!ownerId) return () => {
      alive = false;
    };

    // `doctor_profiles.gallery_photo_paths` is the authoritative ordered
    // gallery across the editor and profile routes. Never allow an expired
    // local preview to resurrect an image removed in the editor.
    (async () => {
      const { data: profileRow, error: profileError } = await supabase
        .from("doctor_profiles")
        .select("gallery_photo_paths")
        .eq("user_id", ownerId)
        .maybeSingle();
      if (profileError || !alive) return;

      const dbPaths = Array.isArray(profileRow?.gallery_photo_paths)
        ? profileRow.gallery_photo_paths.map((v) => String(v || "").trim()).filter(Boolean)
        : [];
      if (!dbPaths.length) {
        setImages([]);
        return;
      }

      const rows = await Promise.all(
        dbPaths.map(async (path, idx) => {
          const { data, error } = await supabase.storage
            .from(DOCTOR_GALLERY_BUCKET)
            .createSignedUrl(path, 60 * 60 * 24);

          if (error || !data?.signedUrl) return null;
          return { id: `db-${idx}-${path}`, url: data.signedUrl, path };
        })
      );
      if (alive) setImages(rows.filter(Boolean));
    })().catch(() => {
      if (alive) setImages([]);
    });

    return () => {
      alive = false;
    };
  }, [profile?.id, profile?.user_id, profile?.doctor_id]);

  return images;
}

// ---------------------------------------------------------------------------
// View-model mapper – swallows EditIntro + EditPages + previews
// ---------------------------------------------------------------------------

function buildDoctorView(profile, t = (key, fallback) => fallback, languageCode = "en") {
  const firstNonEmptyArray = (...values) => {
    for (const value of values) {
      const arr = toArray(value);
      if (Array.isArray(arr) && arr.length > 0) return arr;
    }
    return [];
  };

  const toNumber = (value) => {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const firstNumber = (...values) => {
    for (const value of values) {
      const n = toNumber(value);
      if (n != null) return n;
    }
    return null;
  };

  // 1) Local previews (Edit screens)
  const profileOwnerId = String(profile?.user_id || profile?.doctor_id || profile?.id || "").trim();
  if (profile?.auth_user_id || profile?.current_user_id) {
    assertHydrationOwner({
      authUserId: profile.auth_user_id || profile.current_user_id,
      hydratedOwnerId: profileOwnerId,
      source: "DoctorProfile.buildDoctorView",
      role: "doctor",
      profileType: "doctor",
      mismatchType: "doctor_profile_owner_mismatch",
    });
  }
  const introPreview =
    profile?.__skipLocalIntroPreview || !profileOwnerId
      ? {}
      : loadDoctorIntroPreview(profileOwnerId) || {};
  const consultanciesPreview = readConsultanciesFromCandidateIds(
    profile?.user_id,
    profile?.id,
    profile?.doctor_id,
  );
  const eVisitPreview = loadScopedFromStorage(DOCTOR_EVISIT_KEY, profileOwnerId);
  const servicesPreview =
    loadScopedFromStorage(DOCTOR_PROFILE_SERVICES_KEY, profileOwnerId) ||
    loadScopedFromStorage(DOCTOR_SERVICES_KEY, profileOwnerId);
  const allowDemoGallery =
    profile?.__allowDemoGallery === true ||
    profile?.demo === true ||
    profile?.isDemo === true ||
    profile?.fixture === true;
  // 2) DB / profile trees
  const doctorRoot =
    safeParseJson(profile?.doctor) ||
    safeParseJson(profile?.doctorProfile) ||
    profile?.doctor ||
    profile?.doctorProfile ||
    profile?.settings?.doctor ||
    {};

  const intro =
    safeParseJson(profile?.doctor_intro) ||
    safeParseJson(profile?.doctorIntro) ||
    safeParseJson(doctorRoot?.intro) ||
    profile?.doctor_intro ||
    profile?.doctorIntro ||
    doctorRoot?.intro ||
    {};

  const stats =
    safeParseJson(profile?.doctor_stats) ||
    safeParseJson(profile?.doctorStats) ||
    profile?.doctor_stats ||
    profile?.doctorStats ||
    doctorRoot?.stats ||
    {};

  // 3) Arrays – DB is source of truth; previews only fill gaps
  const consultanciesRaw = firstNonEmptyArray(
    profile?.doctor_consultancies,
    doctorRoot?.consultancies,
    profile?.consultancies,
    profile?.clinics,
    consultanciesPreview,
  );

  const servicesRaw = firstNonEmptyArray(
    profile?.doctor_services,
    doctorRoot?.services,
    profile?.services,
    profile?.service_catalog,
    servicesPreview,
  );

  const galleryRaw = firstNonEmptyArray(
    allowDemoGallery ? profile?.doctor_gallery : [],
    allowDemoGallery ? doctorRoot?.gallery : [],
    allowDemoGallery ? profile?.gallery : [],
    allowDemoGallery ? profile?.photos : [],
  );

  const languages = orderLanguageList(
    firstNonEmptyArray(
      profile?.languages_spoken,
      profile?.languages,
      doctorRoot?.languages_spoken,
      doctorRoot?.languages,
      intro?.languages_spoken,
      intro?.languages,
      introPreview?.languages,
    )
  );

  const eVisitRaw =
    safeParseJson(profile?.doctor_evisit) ||
    safeParseJson(profile?.e_visit) ||
    safeParseJson(profile?.eVisit) ||
    safeParseJson(doctorRoot?.eVisit) ||
    profile?.doctor_evisit ||
    profile?.e_visit ||
    profile?.eVisit ||
    (typeof profile?.evisit_enabled === "boolean" || profile?.evisit_availability
      ? {
          enabled: profile?.evisit_enabled,
          availability_text: profile?.evisit_availability || "",
          frequencyLabel: profile?.evisit_availability || "",
          status: profile?.evisit_enabled ? "On" : "Off",
        }
      : null) ||
    doctorRoot?.eVisit ||
    (eVisitPreview && (safeParseJson(eVisitPreview) || eVisitPreview)) ||
    {};

  // 4) Experience – explicit value first; never infer from license issue date.
  const explicitExperience = firstNumber(
    profile?.years_experience,
    profile?.years_of_experience,
    stats?.experience_years,
    stats?.years_experience,
    intro?.experience_years,
    intro?.years_experience,
    introPreview?.years_experience,
    introPreview?.years_of_experience,
  );

  const experienceYears =
    Number.isFinite(explicitExperience) && Number(explicitExperience) > 0
      ? Math.floor(Number(explicitExperience))
      : null;

  const experienceDisplay =
    Number.isFinite(experienceYears) && Number(experienceYears) > 0
      ? Number(experienceYears) === 1
        ? t("doctor.profile.stats.experienceOneYear", "1 year")
        : t("doctor.profile.stats.experienceYears", "{count} years", {
            count: Math.floor(Number(experienceYears)),
          })
      : "—";

  // 5) Header – prefer DB, preview only as fallback
  const previewName = (introPreview.name || "").trim();
  const nameFromParts = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const dbName =
    intro.full_name ||
    intro.fullName ||
    intro.name ||
    intro.display_name ||
    intro.displayName ||
    profile?.full_name ||
    profile?.fullName ||
    profile?.name ||
    profile?.display_name ||
    profile?.displayName ||
    nameFromParts ||
    "";
  const addressedName = resolveAddressedProfileName(
    "doctor",
    {
      ...profile,
      ...intro,
      salutation: intro?.salutation || profile?.salutation,
      last_name: intro?.last_name || profile?.last_name,
      family_name: intro?.family_name || profile?.family_name,
    },
    "",
  );
  const name =
    addressedName ||
    dbName ||
    previewName ||
    t("doctor.profile.fallback.doctor", "Doctor");

  const previewSpecialty = (introPreview.specialtySummary || "").trim();
  const specialtyArray =
    (Array.isArray(intro?.specializations) ? intro.specializations : null) ||
    (Array.isArray(intro?.specialization) ? intro.specialization : null) ||
    (Array.isArray(intro?.specialties) ? intro.specialties : null) ||
    (Array.isArray(doctorRoot?.specializations) ? doctorRoot.specializations : null) ||
    (Array.isArray(doctorRoot?.specialization) ? doctorRoot.specialization : null) ||
    (Array.isArray(doctorRoot?.specialties) ? doctorRoot.specialties : null) ||
    (Array.isArray(profile?.specializations) ? profile.specializations : null) ||
    (Array.isArray(profile?.specialization) ? profile.specialization : null) ||
    (Array.isArray(profile?.specialties) ? profile.specialties : null) ||
    null;

  const specialtyFromArray = Array.isArray(specialtyArray)
    ? specialtyArray.filter(Boolean).join(", ").trim()
    : "";

  const dbSpecialty =
    specialtyFromArray ||
    intro.primary_specialty ||
    intro.primarySpecialty ||
    intro.specialty ||
    intro.main_specialty ||
    doctorRoot?.primary_specialty ||
    doctorRoot?.specialty ||
    doctorRoot?.main_specialty ||
    profile?.primary_specialty ||
    profile?.specialty ||
    "";

  const specialty =
    dbSpecialty ||
    previewSpecialty ||
    t("doctor.profile.fallback.specialtyMissing", "Specialty not added yet");

  const dbAbout =
    intro.about_you ||
    profile?.about_you ||
    intro.aboutYou ||
    profile?.aboutYou ||
    intro.about ||
    doctorRoot?.about ||
    "";

  const about =
    dbAbout ||
    (introPreview.about || "").trim();

  // Focus tags – preview → DB/profile → blueprint
  const focusPreviewRaw =
    introPreview.focusTags ||
    introPreview.focus ||
    introPreview.focusAreas;
  const focusLocalStored = loadScopedFromStorage(DOCTOR_FOCUS_KEY, profileOwnerId);
  const focusLocalRaw = Array.isArray(focusLocalStored)
    ? focusLocalStored
    : Array.isArray(focusLocalStored?.focus_tags)
    ? focusLocalStored.focus_tags
    : Array.isArray(focusLocalStored?.items)
    ? focusLocalStored.items
    : [];

  const focusFromDb =
    intro.focus_tags ||
    intro.focusTags ||
    intro.focus ||
    doctorRoot?.focus_tags ||
    profile?.focus_tags;
  const focusRaw =
    (Array.isArray(focusFromDb) && focusFromDb.length ? focusFromDb : null) ||
    (Array.isArray(focusPreviewRaw) && focusPreviewRaw.length
      ? focusPreviewRaw
      : null) ||
    (Array.isArray(focusLocalRaw) && focusLocalRaw.length ? focusLocalRaw : null);

  const focus = Array.isArray(focusRaw)
    ? focusRaw
        .flatMap((item) => {
          if (typeof item === "string" && item.includes(",")) {
            return item
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean);
          }
          return [String(item || "").trim()].filter(Boolean);
        })
        .filter(Boolean)
    : [];

  // 6) Virtual consultation – merge preview with DB
  const eVisit = eVisitRaw || {};

  const eVisitLabel = t(
    "doctor.profile.sections.virtualConsultations",
    isFrenchLanguageCode(languageCode) ? "Consultations virtuelles" : "Virtual consultations"
  );

  const eVisitEnabled =
    typeof eVisit.enabled === "boolean"
      ? eVisit.enabled
      : eVisit.status === "On" || eVisit.status === true;

  const eVisitStatus = eVisitEnabled
    ? t("common.on", isFrenchLanguageCode(languageCode) ? "Activé" : "On")
    : t("common.off", isFrenchLanguageCode(languageCode) ? "Désactivé" : "Off");

  const eVisitAvailability = localizeConsultancyValue(
    "availability",
    eVisit.availability_text ||
      eVisit.availabilityText ||
      eVisit.schedule ||
      eVisit.frequencyLabel ||
      resolveAvailabilitySummary(profile, t) ||
      t("doctor.profile.fallback.notSet", "Not set"),
    t,
  );
  const availabilitySummary = resolveAvailabilitySummary(profile, t);
  const paymentTermsSummary = resolvePaymentTermsSummary(profile, t);
  const eVisitPriceLabel = resolveEVisitPriceLabel(profile);

  // 7) Consultancies
  const consultancies =
    consultanciesRaw && consultanciesRaw.length > 0
      ? consultanciesRaw.map((c, idx) => ({
          id: c.id ?? `consultancy-${idx}`,
          name:
            c.name ||
            c.facility_name ||
            c.facilityName ||
            t("doctor.profile.fallback.facility", "Facility"),
          address:
            c.address_line ||
            c.addressLine ||
            c.address ||
            "",
          patientsAge: localizeConsultancyValue(
            "patientsAge",
            c.patient_age_range ||
              c.patientAgeRange ||
              c.patients_age ||
              t("doctor.profile.fallback.notSet", "Not set"),
            t,
          ),
          availability: localizeConsultancyValue(
            "availability",
            c.availability_text ||
              c.availabilityText ||
              c.schedule ||
              availabilitySummary ||
              t("doctor.profile.fallback.notSet", "Not set"),
            t,
          ),
          insurancePolicy: localizeConsultancyValue(
            "insurancePolicy",
            c.insurance_policy ||
              c.insurancePolicy ||
              t("doctor.profile.fallback.notSet", "Not set"),
            t,
          ),
          paymentTerms: localizeConsultancyValue(
            "paymentTerms",
            c.payment_terms ||
              c.paymentTerms ||
              c.payment_methods ||
              paymentTermsSummary ||
              t("doctor.profile.fallback.notSet", "Not set"),
            t,
          ),
        }))
      : [];

  // 8) Services
  const services =
    servicesRaw && servicesRaw.length > 0
      ? servicesRaw.map((s, idx) => ({
          id: s.id ?? `service-${idx}`,
          name: s.name || s.title || t("doctor.profile.fallback.service", "Service"),
          price: String(s.price ?? s.amount ?? "0.00"),
          currency: s.currency || s.currency_code || "BWP",
        }))
      : [];

  // 9) Gallery – strings or { url } objects
  const gallery =
    galleryRaw && galleryRaw.length > 0
      ? galleryRaw
          .map((g) =>
            typeof g === "string"
              ? g
              : g.url || g.image_url || g.src || null
          )
          .filter(Boolean)
      : [];

  const patientsCount =
    firstNumber(
      profile?.patients_count,
      stats?.patients_count,
      stats?.total_patients,
      intro?.patients,
      intro?.total_patients
    ) ?? 0;

  const ratingValue =
    firstNumber(
      profile?.rating,
      stats?.rating,
      intro?.rating
    );

  // 10) Final object the JSX uses
  return {
    name,
    specialty,
    patients: patientsCount,
    experienceYears,
    experienceDisplay,
    rating: ratingValue,
    ratingDisplay: ratingValue == null ? "—" : Number(ratingValue).toFixed(1),
    about,
    focus,
    consultancies,
    eVisit: {
      label: eVisitLabel,
      status: eVisitStatus,
      availability: eVisitAvailability,
      priceLabel: eVisitPriceLabel,
    },
    languages,
    services,
    gallery,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DoctorProfile() {
  const nav = useNavigate();
  const { t, languageCode } = useLanguage();

  // ✅ one auth object only (do not call useAuth twice)
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const profile = auth.profile || null;
  const authUser = auth.user || null;

  const location = useLocation();
  const queryDoctorId = React.useMemo(() => {
    try {
      return String(new URLSearchParams(location.search || "").get("doctorId") || "").trim();
    } catch {
      return "";
    }
  }, [location.search]);

// ✅ owner view (doctor editing self) vs public view (patient/anon)
const viewerFrom = String(location.state?.from || "").trim().toLowerCase();
const isOwnerView = !(queryDoctorId || location.state?.fromPublic || viewerFrom === "patient");
const canEdit = isOwnerView;
  const publicDoctorUserId = String(
    queryDoctorId ||
      location.state?.doctorUserId ||
      location.state?.doctorId ||
      location.state?.doctor?.id ||
      location.state?.doctorProfile?.user_id ||
      location.state?.doctorProfile?.id ||
      ""
  ).trim();

  const publicDoctorSeed = React.useMemo(() => {
    if (canEdit) return null;

    const doctorState = location.state?.doctor || location.state?.doctorProfile || {};
    if (!doctorState || typeof doctorState !== "object") return null;

    const facilityName = String(
      doctorState?.hospital ||
        doctorState?.facility?.name ||
        doctorState?.facility_name ||
        ""
    ).trim();
    const facilityAddress = String(
      doctorState?.address ||
        doctorState?.facility?.address ||
        doctorState?.facility_address ||
        ""
    ).trim();

    return {
      __skipLocalIntroPreview: true,
      user_id: publicDoctorUserId || String(doctorState?.id || "").trim() || null,
      id: doctorState?.profile_id || doctorState?.doctor_profile_id || null,
      full_name:
        doctorState?.full_name ||
        doctorState?.display_name ||
        doctorState?.name ||
        null,
      display_name:
        doctorState?.display_name ||
        doctorState?.full_name ||
        doctorState?.name ||
        null,
      primary_specialty:
        doctorState?.primary_specialty || doctorState?.specialty || null,
      specialty: doctorState?.specialty || doctorState?.primary_specialty || null,
      profile_image_url:
        doctorState?.profile_image_url ||
        doctorState?.avatar ||
        doctorState?.avatar_url ||
        null,
      avatar_url:
        doctorState?.avatar_url ||
        doctorState?.profile_image_url ||
        doctorState?.avatar ||
        null,
      verified_status:
        doctorState?.verified_status ||
        doctorState?.verificationStatus ||
        (doctorState?.verified ? "verified" : "pending"),
      years_of_experience:
        typeof doctorState?.years_of_experience !== "undefined"
          ? doctorState.years_of_experience
          : doctorState?.yearsExperience,
      patients_count:
        typeof doctorState?.patients_count !== "undefined"
          ? doctorState.patients_count
          : doctorState?.patients,
      rating: doctorState?.rating ?? null,
      languages_spoken:
        orderLanguageList(doctorState?.languages_spoken || doctorState?.languages || null),
      doctor_consultancies:
        facilityName || facilityAddress
          ? [
              {
                facility_name: facilityName || null,
                facility_address: facilityAddress || null,
                facility_city:
                  doctorState?.facility?.city || doctorState?.facility_city || null,
                facility_country:
                  doctorState?.facility?.country ||
                  doctorState?.facility_country ||
                  null,
                insurance_policy:
                  doctorState?.insurancePolicy ||
                  doctorState?.insurance_policy ||
                  doctorState?.facility?.insurancePolicy ||
                  doctorState?.facility?.insurance_policy ||
                  null,
              },
            ]
          : [],
      doctor_services:
        doctorState?.serviceLabel || doctorState?.serviceName
          ? [
              {
                name: doctorState?.serviceLabel || doctorState?.serviceName || "Service",
                price:
                  Number(doctorState?.servicePrice ?? doctorState?.amount ?? doctorState?.serviceLabelPrice) ||
                  0,
                currency: doctorState?.currency || "ZAR",
                duration_minutes:
                  doctorState?.serviceDurationMinutes ||
                  doctorState?.durationMinutes ||
                  30,
              },
            ]
          : [],
    };
  }, [canEdit, location.state, publicDoctorUserId]);

  const [publicDoctorProfile, setPublicDoctorProfile] = React.useState(null);
  const [publicProfileChecking, setPublicProfileChecking] = React.useState(!canEdit);
  const [publicProfileUnavailable, setPublicProfileUnavailable] = React.useState(false);

  const BYPASS =
  import.meta.env.DEV &&
  import.meta.env.VITE_BYPASS_VERIFICATION === "1";

  React.useEffect(() => {
    if (canEdit) {
      setPublicDoctorProfile(null);
      setPublicProfileChecking(false);
      setPublicProfileUnavailable(false);
      return;
    }

    setPublicDoctorProfile(null);
    setPublicProfileChecking(true);
    setPublicProfileUnavailable(false);

    let alive = true;

    async function hydratePublicDoctorProfile() {
      if (!publicDoctorUserId) {
        if (alive) {
          setPublicProfileChecking(false);
          setPublicProfileUnavailable(true);
        }
        return;
      }

      try {
        const { data: userProfileRow, error: userProfileError } = await supabase
          .from("doctor_profiles")
          .select("*")
          .eq("user_id", publicDoctorUserId)
          .maybeSingle();

        let profileRow = userProfileError ? null : userProfileRow;
        if (!profileRow) {
          const { data: idProfileRow, error: idProfileError } = await supabase
            .from("doctor_profiles")
            .select("*")
            .eq("id", publicDoctorUserId)
            .maybeSingle();
          profileRow = idProfileError ? null : idProfileRow;
        }

        const doctorIds = Array.from(
          new Set(
            [
              publicDoctorUserId,
              profileRow?.user_id,
              profileRow?.id,
              publicDoctorSeed?.user_id,
              publicDoctorSeed?.id,
            ]
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          )
        );

        const canonicalDoctorUserId =
          String(profileRow?.user_id || publicDoctorSeed?.user_id || publicDoctorUserId || "").trim();
        const [
          accountResult,
          consultanciesResult,
          servicesResult,
          derivedPatientsCount,
          publicPatientsCount,
        ] = await Promise.all([
          supabase
            .from("profiles")
            .select("user_id,account_classification,exclude_from_public_discovery")
            .eq("user_id", canonicalDoctorUserId)
            .maybeSingle(),
          supabase
            .from("doctor_consultancies")
            .select("*")
            .in("doctor_id", doctorIds)
            .order("is_primary", { ascending: false })
            .order("sort_order", { ascending: true })
            .order("updated_at", { ascending: false }),
          supabase
            .from("doctor_services")
            .select("id,name,price,currency,duration_minutes,is_active,sort_order,updated_at")
            .in("doctor_id", doctorIds)
            .eq("is_active", true)
            .order("sort_order", { ascending: true })
            .order("updated_at", { ascending: false }),
          fetchDoctorUniquePatientCount(supabase, doctorIds).catch(() => null),
          fetchDoctorPublicPatientCount(supabase, canonicalDoctorUserId),
        ]);

        if (!alive) return;

        const accountRow = accountResult?.data || {};
        const consultancies = consultanciesResult?.data;
        const services = servicesResult?.data;
        const storedPatientsCount = Number(
          profileRow?.patients_count ?? publicDoctorSeed?.patients_count
        );
        const publicCount = Number(publicPatientsCount);
        const rlsCount = Number(derivedPatientsCount);
        const countCandidates = [publicCount, storedPatientsCount, rlsCount].filter(Number.isFinite);
        const resolvedPatientsCount =
          countCandidates.length > 0 ? Math.max(...countCandidates) : null;

        const nextPublicProfile = {
          ...(publicDoctorSeed || {}),
          ...(profileRow || {}),
          account_classification:
            accountRow?.account_classification ||
            profileRow?.account_classification ||
            publicDoctorSeed?.account_classification ||
            null,
          exclude_from_public_discovery:
            accountRow?.exclude_from_public_discovery ??
            profileRow?.exclude_from_public_discovery ??
            publicDoctorSeed?.exclude_from_public_discovery ??
            false,
          __skipLocalIntroPreview: true,
          languages_spoken: orderLanguageList(
            profileRow?.languages_spoken || publicDoctorSeed?.languages_spoken || null
          ),
          patients_count:
            resolvedPatientsCount != null
              ? resolvedPatientsCount
              : profileRow?.patients_count,
          doctor_consultancies:
            Array.isArray(consultancies) && consultancies.length > 0
              ? consultancies
              : publicDoctorSeed?.doctor_consultancies || [],
          doctor_services:
            Array.isArray(services) && services.length > 0
              ? services
              : publicDoctorSeed?.doctor_services || [],
        };

        if (!isPublicDoctorProfileViewable(nextPublicProfile)) {
          setPublicDoctorProfile(null);
          setPublicProfileUnavailable(true);
          return;
        }

        setPublicDoctorProfile(nextPublicProfile);
        setPublicProfileUnavailable(false);
      } catch (err) {
        console.error("Public doctor profile hydrate failed", err);
        if (alive) setPublicProfileUnavailable(true);
      } finally {
        if (alive) setPublicProfileChecking(false);
      }
    }

    hydratePublicDoctorProfile();
    return () => {
      alive = false;
    };
  }, [canEdit, publicDoctorSeed, publicDoctorUserId]);

  const ownerDoctorId = canEdit ? (authUser?.id || profile?.user_id || null) : null;
  const devCoverKey = ownerDoctorId ? `${DEV_COVER_KEY_PREFIX}${ownerDoctorId}` : "";
  const devCover =
    (BYPASS &&
      typeof window !== "undefined" &&
      devCoverKey &&
      (() => {
        try {
          return localStorage.getItem(devCoverKey) || "";
        } catch {
          return "";
        }
      })()) ||
    "";

  const devPhoto =
    (BYPASS &&
      typeof window !== "undefined" &&
      (() => {
        try {
          return localStorage.getItem(DEV_PHOTO_KEY) || "";
        } catch {
          return "";
        }
      })()) ||
    "";

  // Cover URL (UI driver). Updates instantly after upload.
  const [coverUrl, setCoverUrl] = React.useState(null);

  const sourceProfile = canEdit ? profile : publicDoctorProfile;

  const COVER_IMAGE =
  sourceProfile?.cover_image_url || DEFAULT_DOCTOR_COVER_IMAGE;

  // Doctor avatars are PUBLIC (patients must browse doctors)
const photoUrl = resolveDoctorAvatarSrc(
  sourceProfile,
  devPhoto || "/icons/Profile_Avatar_borderless.svg",
);

const rawVerifiedStatus = String(sourceProfile?.verified_status || "").toLowerCase().trim();
const isDoctorVerified = rawVerifiedStatus === "verified";
const verificationBadgeSrc = isDoctorVerified
  ? ICON_VERIFIED
  : ICON_PENDING_VERIFICATION;
const verificationBadgeAlt = isDoctorVerified
  ? t("common.verified", "Verified")
  : t("common.pendingVerification", "Pending verification");

  const [dbConsultancies, setDbConsultancies] = React.useState(null);
  const [dbConsultanciesLoaded, setDbConsultanciesLoaded] = React.useState(false);
  const [dbServices, setDbServices] = React.useState(null);
  const [dbServicesLoaded, setDbServicesLoaded] = React.useState(false);
  const [dbFocusTags, setDbFocusTags] = React.useState(null);
  const [dbDoctorPatch, setDbDoctorPatch] = React.useState(null);
  const [consultancyActionTarget, setConsultancyActionTarget] = React.useState(null);
  const [consultancyDeleteOpen, setConsultancyDeleteOpen] = React.useState(false);
  const [consultancyDeleting, setConsultancyDeleting] = React.useState(false);

  React.useEffect(() => {
    let alive = true;

    async function hydrateConsultanciesFromDb() {
      try {
        const candidateDoctorIds = resolveDoctorIdCandidates(
          ownerDoctorId,
          authUser?.id,
          profile?.user_id,
          profile?.id,
          profile?.doctor_id,
        );
        if (!candidateDoctorIds.length) {
          if (alive) {
            setDbConsultancies([]);
            setDbConsultanciesLoaded(true);
          }
          return;
        }

        const { data: ownProfile } = await supabase
          .from("doctor_profiles")
          .select("id,user_id")
          .in("user_id", candidateDoctorIds)
          .limit(1)
          .maybeSingle();

        const resolvedDoctorIds = resolveDoctorIdCandidates(
          ...candidateDoctorIds,
          ownProfile?.id,
          ownProfile?.user_id,
        );

        const { data, error } = await supabase
          .from("doctor_consultancies")
          .select(
            "id,facility_name,facility_address,facility_city,facility_country,patients_age,availability_text,insurance_policy,payment_terms,is_primary,sort_order,updated_at",
          )
          .in("doctor_id", resolvedDoctorIds)
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false });

        if (!alive) return;

        let rows = !error && Array.isArray(data)
          ? data.map((row) => ({
              ...row,
              name: row.facility_name || "",
              address: row.facility_address || "",
              address_line: row.facility_address || "",
              city: row.facility_city || "",
              country: row.facility_country || "",
              patient_age_range: row.patients_age || "",
              payment_methods: row.payment_terms || "",
            }))
          : [];

        // Backward-compat: some older flows used doctor_consulting_locations.
        if (rows.length === 0) {
          const { data: legacyRows, error: legacyErr } = await supabase
            .from("doctor_consulting_locations")
            .select(
              "id,doctor_id,facility_name,street_address,city,country,patients_age,availability,insurance_policy,payment_terms,updated_at",
            )
            .in("doctor_id", resolvedDoctorIds)
            .order("updated_at", { ascending: false });

          if (!legacyErr && Array.isArray(legacyRows) && legacyRows.length > 0) {
            rows = legacyRows.map((row) => ({
              id: row.id,
              name: row.facility_name || "",
              facility_name: row.facility_name || "",
              address: row.street_address || "",
              facility_address: row.street_address || "",
              address_line: row.street_address || "",
              city: row.city || "",
              facility_city: row.city || "",
              country: row.country || "",
              facility_country: row.country || "",
              patients_age: row.patients_age || "",
              patient_age_range: row.patients_age || "",
              availability_text: row.availability || "",
              insurance_policy: row.insurance_policy || "",
              payment_terms: row.payment_terms || "",
              payment_methods: row.payment_terms || "",
              updated_at: row.updated_at || null,
            }));
          }
        }

        setDbConsultancies(rows);

        if (typeof window !== "undefined") {
          try {
            const targetIds = resolveDoctorIdCandidates(
              ownerDoctorId,
              auth.user?.id,
              authUser?.id,
              profile?.user_id,
              profile?.id,
              profile?.doctor_id,
            );
            for (const id of targetIds) {
              writeDoctorConsultancies(id, rows);
            }
          } catch {
            // non-fatal
          }
        }
      } catch (err) {
        console.error("Doctor consultancies hydrate failed", err);
      } finally {
        if (alive) setDbConsultanciesLoaded(true);
      }
    }

    hydrateConsultanciesFromDb();
    return () => {
      alive = false;
    };
  }, [ownerDoctorId, authUser?.id, profile?.user_id, profile?.id, profile?.doctor_id]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateServicesFromDb() {
      try {
        const userId = ownerDoctorId;
        if (!userId) {
          if (alive) {
            setDbServices([]);
            setDbServicesLoaded(true);
          }
          return;
        }

        const { data, error } = await supabase
          .from("doctor_services")
          .select("id,name,price,currency,duration_minutes,is_active,sort_order,updated_at")
          .eq("doctor_id", userId)
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false });

        if (error || !alive) return;

        let rows = Array.isArray(data)
          ? data.map((row) => ({
              id: row.id,
              name: row.name || "",
              price: Number(row.price || 0),
              currency: row.currency || "BWP",
              durationMinutes: Number(row.duration_minutes || 30) || 30,
            }))
          : [];

        // Backward-compat: some historical flows wrote service pricing
        // into doctor_service_prices instead of doctor_services.
        if (rows.length === 0) {
          const { data: legacyRows, error: legacyErr } = await supabase
            .from("doctor_service_prices")
            .select("id,doctor_user_id,doctor_id,service_code,price,currency")
            .eq("doctor_user_id", userId)
            .eq("is_active", true)
            .order("updated_at", { ascending: false });

          if (!legacyErr && Array.isArray(legacyRows) && legacyRows.length > 0) {
            rows = legacyRows.map((row, idx) => ({
              id: row.id || `legacy-service-${idx}`,
              name: String(row.service_code || "").trim() || t("doctor.profile.fallback.service", "Service"),
              price: Number(row.price || 0),
              currency: String(row.currency || "BWP").trim() || "BWP",
              durationMinutes: 30,
            }));
          }
        }

        setDbServices(rows);

        if (typeof window !== "undefined") {
          try {
            const payload = JSON.stringify(rows);
            if (userId) {
              window.localStorage.setItem(`${DOCTOR_SERVICES_KEY}:${userId}`, payload);
              window.localStorage.setItem(`${DOCTOR_PROFILE_SERVICES_KEY}:${userId}`, payload);
            }
          } catch {
            // non-fatal
          }
        }
      } catch (err) {
        console.error("Doctor services hydrate failed", err);
      } finally {
        if (alive) setDbServicesLoaded(true);
      }
    }

    hydrateServicesFromDb();
    return () => {
      alive = false;
    };
  }, [ownerDoctorId]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateDoctorPatchFromDb() {
      try {
        const userId = ownerDoctorId;
        if (!userId) return;

        const { data, error } = await supabase
          .from("doctor_profiles")
          .select(
            "focus_tags,practice_mode,evisit_enabled,evisit_availability,availability,appointment_settings,consultation_fees,payout_meta,patients_count,years_experience,years_of_experience,rating",
          )
          .eq("user_id", userId)
          .maybeSingle();

        if (error || !alive || !data) return;

        const patch = { ...data };

        const derivedPatientsCount = await fetchDoctorUniquePatientCount(
          supabase,
          userId,
        );

        if (derivedPatientsCount != null) {
          patch.patients_count = derivedPatientsCount;
          await supabase
            .from("doctor_profiles")
            .update({
              patients_count: derivedPatientsCount,
              updated_at: new Date().toISOString(),
            })
            .eq("user_id", userId);
        }

        setDbDoctorPatch(patch);
      } catch (err) {
        console.error("Doctor profile patch hydrate failed", err);
      }
    }

    hydrateDoctorPatchFromDb();
    return () => {
      alive = false;
    };
  }, [ownerDoctorId]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFocusFromDb() {
      try {
        const userId = ownerDoctorId;
        if (!userId) return;

        const { data, error } = await supabase
          .from("doctor_profiles")
          .select("focus_tags")
          .eq("user_id", userId)
          .maybeSingle();

        if (error || !alive) return;
        const next = Array.isArray(data?.focus_tags) ? data.focus_tags : [];
        setDbFocusTags(next);
      } catch (err) {
        console.error("Doctor focus hydrate failed", err);
      }
    }

    hydrateFocusFromDb();
    return () => {
      alive = false;
    };
  }, [ownerDoctorId]);

  const profileForView = React.useMemo(() => {
    if (!sourceProfile) return sourceProfile;
    const next = { ...sourceProfile };
    if (canEdit && ownerDoctorId) {
      next.user_id = String(next.user_id || ownerDoctorId);
      next.doctor_id = String(next.doctor_id || ownerDoctorId);
    }
    if (dbDoctorPatch && typeof dbDoctorPatch === "object") {
      Object.assign(next, dbDoctorPatch);
    }
    if (canEdit && dbConsultanciesLoaded) {
      const localRows = readConsultanciesFromCandidateIds(
        ownerDoctorId,
        auth.user?.id,
        authUser?.id,
        profile?.user_id,
        profile?.id,
        profile?.doctor_id,
      );
      next.doctor_consultancies = mergeConsultancyRowsById(dbConsultancies, localRows);
    }
    if (canEdit && dbServicesLoaded) {
      const localRows =
        toArray(loadScopedFromStorage(DOCTOR_PROFILE_SERVICES_KEY, ownerDoctorId)).length > 0
          ? toArray(loadScopedFromStorage(DOCTOR_PROFILE_SERVICES_KEY, ownerDoctorId))
          : toArray(loadScopedFromStorage(DOCTOR_SERVICES_KEY, ownerDoctorId));
      next.doctor_services = Array.isArray(dbServices) && dbServices.length
        ? dbServices
        : localRows;
    }
    if (canEdit && Array.isArray(dbFocusTags)) {
      next.focus_tags = dbFocusTags;
    }
    return next;
  }, [
    sourceProfile,
    dbConsultancies,
    dbConsultanciesLoaded,
    dbDoctorPatch,
    dbServices,
    dbServicesLoaded,
    dbFocusTags,
    canEdit,
    ownerDoctorId,
    auth.user?.id,
  ]);

  const doctor = React.useMemo(
    () => buildDoctorView(profileForView, t, languageCode),
    [languageCode, profileForView, t]
  );
  const profilePracticeMode = normalizePracticeMode(profileForView?.practice_mode);
  const profileSectionOrder =
    profilePracticeMode
      ? PROFILE_SECTION_ORDER_BY_PRACTICE_MODE[profilePracticeMode] ||
        PROFILE_SECTION_ORDER_DEFAULT
      : PROFILE_SECTION_ORDER_DEFAULT;
  const hasConsultancyRows =
    Array.isArray(doctor?.consultancies) && doctor.consultancies.length > 0;
  const patientInsuranceDisplay = React.useMemo(
    () =>
      resolveInsuranceDisplay(
        (doctor?.consultancies || []).map((consultancy) => consultancy?.insurancePolicy),
        profileForView?.insurance_policy,
        profileForView?.insurancePolicy
      ),
    [doctor?.consultancies, profileForView]
  );

  const handleGoToEVisit = () => {
    nav("/settings/doctor/p/e-visit");
  };

// ------------------------------------------------------------
// Cover photo: picker -> crop (3:1) -> upload -> save -> UI update
// ------------------------------------------------------------

const [coverPickerOpen, setCoverPickerOpen] = React.useState(false);
const [coverSrc, setCoverSrc] = React.useState(null); // dataURL for crop modal
const [showCoverCrop, setShowCoverCrop] = React.useState(false);
const [coverUploading, setCoverUploading] = React.useState(false);
const [coverError, setCoverError] = React.useState("");
const [coverDeleteOpen, setCoverDeleteOpen] = React.useState(false);

const coverFileInputRef = React.useRef(null);
const coverCameraInputRef = React.useRef(null);
const shareMenuRef = React.useRef(null);
const [shareMenuOpen, setShareMenuOpen] = React.useState(false);

function openCoverPicker() {
  if (coverUploading) return;
  setCoverError("");
  setCoverPickerOpen(true);
}

function closeCoverPicker() {
  setCoverPickerOpen(false);
}

function handleAvatarUploadClick() {
  if (coverUploading) return;
  coverFileInputRef.current?.click();
}

function handleAvatarTakePhotoClick() {
  if (coverUploading) return;
  coverCameraInputRef.current?.click();
}

const hasCustomCover = Boolean(
  (coverUrl && coverUrl !== DEFAULT_DOCTOR_COVER_IMAGE) ||
    (!coverUrl &&
      (devCover || sourceProfile?.cover_photo_path || sourceProfile?.cover_image_url)),
);

function requestCoverRemoval() {
  if (coverUploading || !hasCustomCover) return;
  closeCoverPicker();
  setCoverDeleteOpen(true);
}

async function handleRemoveCover() {
  if (coverUploading) return;

  if (BYPASS) {
    try {
      if (devCoverKey) localStorage.removeItem(devCoverKey);
      setCoverUrl(DEFAULT_DOCTOR_COVER_IMAGE);
      setCoverDeleteOpen(false);
    } catch (err) {
      console.error("[cover] local removal failed:", err);
      setCoverError(
        t("doctor.profile.cover.errors.remove", "We couldn’t remove your cover photo. Please try again."),
      );
    }
    return;
  }

  try {
    setCoverUploading(true);
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user) throw new Error("Not authenticated");

    const filePath = `${user.id}/cover.jpg`;
    const { error: deleteErr } = await supabase.storage
      .from("cover-images")
      .remove([filePath]);
    if (deleteErr) throw deleteErr;

    const { error: profileErr } = await supabase
      .from("doctor_profiles")
      .update({ cover_photo_path: null, cover_image_url: null })
      .eq("user_id", user.id);
    if (profileErr) throw profileErr;

    // Keep the profile coherent before auth hydration completes.
    setCoverUrl(DEFAULT_DOCTOR_COVER_IMAGE);
    setCoverDeleteOpen(false);
    try {
      await auth?.refreshProfile?.();
      localStorage.setItem("afrimed:profile:refresh", String(Date.now()));
    } catch {
      // The immediate default is already rendered; a later hydration can retry.
    }
  } catch (err) {
    console.error("[cover] removal failed:", err);
    setCoverError(
      t("doctor.profile.cover.errors.remove", "We couldn’t remove your cover photo. Please try again."),
    );
  } finally {
    setCoverUploading(false);
  }
}

// File -> dataURL -> open crop modal
function handleCoverFileSelected(file) {
  if (!file) return;

  const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!ALLOWED.includes(file.type)) {
    setCoverError(t("doctor.profile.cover.errors.fileType", "Please upload a JPG, PNG or WebP image."));
    return;
  }

  // Optional: keep it strict (you set 3MB bucket)
  const MAX = 3 * 1024 * 1024;
  if (file.size > MAX) {
    setCoverError(t("doctor.profile.cover.errors.fileSize", "Max cover file size is 3MB."));
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    setCoverSrc(String(reader.result)); // dataURL
    setShowCoverCrop(true);
    closeCoverPicker();
  };
  reader.onerror = () => {
    console.error("Cover FileReader error:", reader.error);
    setCoverError(t("doctor.profile.cover.errors.fileRead", "Could not read the selected image."));
  };
  reader.readAsDataURL(file);
}

  React.useEffect(() => {
    if (!coverError) return undefined;
    const timer = setTimeout(() => setCoverError(""), 4200);
    return () => clearTimeout(timer);
  }, [coverError]);

  async function handleDeleteConsultancy() {
    const targetId = String(consultancyActionTarget?.id || "").trim();
    if (!targetId || consultancyDeleting) return;

    const prevRows = Array.isArray(dbConsultancies)
      ? dbConsultancies
      : Array.isArray(profileForView?.doctor_consultancies)
      ? profileForView.doctor_consultancies
      : [];
    const nextRows = prevRows.filter((row) => String(row?.id || "") !== targetId);
    setDbConsultancies(nextRows);

    if (typeof window !== "undefined") {
      try {
        writeDoctorConsultancies(ownerDoctorId || auth.user?.id || "", nextRows);
      } catch {
        // non-fatal
      }
    }

    setConsultancyDeleteOpen(false);
    setConsultancyActionTarget(null);

    try {
      setConsultancyDeleting(true);
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr) throw authErr;
      if (!user?.id) return;

      const { error } = await supabase
        .from("doctor_consultancies")
        .delete()
        .eq("id", targetId)
        .eq("doctor_id", user.id);

      if (error) throw error;
    } catch (err) {
      console.error("Consultancy delete failed", err);
      setDbConsultancies(prevRows);
    } finally {
      setConsultancyDeleting(false);
    }
  }

  const focusChips = React.useMemo(
    () => localizeDisplayList(doctor.focus, languageCode, "focus"),
    [doctor.focus, languageCode],
  );

  const galleryImagesFromPreview = useDoctorGalleryPreview(profileForView || sourceProfile);
  const galleryImagesFromDoctor = React.useMemo(() => {
    const raw = Array.isArray(doctor.gallery) ? doctor.gallery : [];
    return raw
      .map((item, idx) => {
        const url =
          typeof item === "string"
            ? item
            : item?.url || item?.image_url || item?.src || "";
        const path =
          typeof item === "object" && item
            ? item.path || item.storage_path || ""
            : "";
        return url ? { id: item?.id || `doctor-gallery-${idx}`, url, path } : null;
      })
      .filter(Boolean);
  }, [doctor.gallery]);
  const galleryImages = React.useMemo(() => {
    const merged = [];
    const seen = new Set();
    const source = galleryImagesFromPreview.length > 0 ? galleryImagesFromPreview : galleryImagesFromDoctor;
    source.forEach((img, idx) => {
      const key = normalizeGalleryIdentity(img, idx);
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push({
        id: img?.id || `gallery-${idx}`,
        url: img?.url,
        path: img?.path || "",
      });
    });
    return merged;
  }, [galleryImagesFromDoctor, galleryImagesFromPreview]);
  const galleryPreviewLimit = 3;
  const [publicGalleryOpen, setPublicGalleryOpen] = React.useState(false);
  const galleryActionLabel = galleryImages.length > galleryPreviewLimit
    ? t("common.showAll", "Show all")
    : null;
  const handleGalleryAction = React.useCallback(() => {
    if (canEdit) {
      nav("/settings/doctor/p/gallery");
      return;
    }
    setPublicGalleryOpen(true);
  }, [canEdit, nav]);
  const publicDoctorName = React.useMemo(() => {
    if (canEdit) return doctor.name;

    const explicitSurname = String(
      profileForView?.last_name ||
        profileForView?.lastName ||
        profileForView?.surname ||
        sourceProfile?.last_name ||
        sourceProfile?.lastName ||
        sourceProfile?.surname ||
        ""
    ).trim();

    const baseName = String(doctor.name || "").replace(/^dr\.?\s+/i, "").trim();
    const derivedSurname =
      explicitSurname ||
      baseName.split(/\s+/).filter(Boolean).slice(-1)[0] ||
      t("doctor.profile.fallback.doctor", "Doctor");

    return `Dr ${derivedSurname}`;
  }, [
    canEdit,
    doctor.name,
    profileForView?.lastName,
    profileForView?.last_name,
    profileForView?.surname,
    sourceProfile?.lastName,
    sourceProfile?.last_name,
    sourceProfile?.surname,
  ]);
  const shareDoctorId = String(
    ownerDoctorId ||
      profileForView?.user_id ||
      sourceProfile?.user_id ||
      profileForView?.id ||
      sourceProfile?.id ||
      ""
  ).trim();
  const doctorProfileShareUrl = React.useMemo(() => {
    const path = shareDoctorId
      ? `/share/doctor/${encodeURIComponent(shareDoctorId)}`
      : "/share/doctor";
    return `https://staging.afrimed.tech${path}`;
  }, [shareDoctorId]);
  const shareText = React.useMemo(() => {
    return t("doctor.profile.share.intent", "I’m on AfriMed™. Sign up to book a consultation with me.");
  }, [t]);
  const socialShareOptions = React.useMemo(() => {
    const encodedUrl = encodeURIComponent(doctorProfileShareUrl);
    const encodedFacebookUrl = encodeURIComponent(
      `${doctorProfileShareUrl}?v=doctor-share-v2`
    );
    return [
      {
        key: "x",
        label: "X",
        icon: ICON_SHARE_X,
        href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(shareText)}`,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: ICON_SHARE_FACEBOOK,
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedFacebookUrl}`,
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        icon: ICON_SHARE_LINKEDIN,
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      },
    ];
  }, [doctorProfileShareUrl, shareText]);
  const handleCopyShareLink = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(doctorProfileShareUrl);
      setShareMenuOpen(false);
    } catch (error) {
      console.warn("[DoctorProfile] Unable to copy share link:", error);
    }
  }, [doctorProfileShareUrl]);
  const handleCopyShareCaption = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${doctorProfileShareUrl}`);
      setShareMenuOpen(false);
    } catch (error) {
      console.warn("[DoctorProfile] Unable to copy share invitation:", error);
    }
  }, [doctorProfileShareUrl, shareText]);

  React.useEffect(() => {
    if (!shareMenuOpen) return undefined;
    function handlePointerDown(event) {
      if (!shareMenuRef.current || shareMenuRef.current.contains(event.target)) return;
      setShareMenuOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [shareMenuOpen]);

  const aboutSectionTitle = canEdit
    ? t("doctor.profile.sections.aboutYou", isFrenchLanguageCode(languageCode) ? "À propos de vous" : "About you")
    : t("doctor.profile.sections.about", isFrenchLanguageCode(languageCode) ? "À propos" : "About");
  const focusSectionTitle = canEdit
    ? t("doctor.profile.sections.yourFocus", isFrenchLanguageCode(languageCode) ? "Vos domaines d’intérêt" : "Your Focus")
    : t("doctor.profile.sections.focus", isFrenchLanguageCode(languageCode) ? "Domaines d’intérêt" : "Focus");
  const servicesSectionTitle = canEdit
    ? t("doctor.profile.sections.yourServices", isFrenchLanguageCode(languageCode) ? "Vos services" : "Your services")
    : t("doctor.profile.sections.services", isFrenchLanguageCode(languageCode) ? "Services" : "Services");
  const editLabel = t("common.edit", isFrenchLanguageCode(languageCode) ? "Modifier" : "Edit");
  const addLabel = t("common.add", isFrenchLanguageCode(languageCode) ? "Ajouter" : "Add");
  const localizedDoctorLanguages = React.useMemo(
    () => localizeDisplayList(doctor.languages, languageCode, "language"),
    [doctor.languages, languageCode],
  );
  const preferredDoctorLanguage = localizedDoctorLanguages[0] || "";
  const otherDoctorLanguages = localizedDoctorLanguages.slice(1);
  const profileSectionPosition = React.useMemo(() => {
    return new Map(profileSectionOrder.map((sectionKey, index) => [sectionKey, index + 1]));
  }, [profileSectionOrder]);
  const getProfileSectionOrder = React.useCallback(
    (sectionKey) => profileSectionPosition.get(sectionKey) || 99,
    [profileSectionPosition],
  );
  const showConsultanciesSection = !(
    profilePracticeMode === "virtual_only"
  );
  const showEVisitSection = !(
    profilePracticeMode === "in_person_only"
  );

  if (!canEdit && publicProfileChecking) {
    return (
      <div className="profile-page-shell flex min-h-dvh items-center justify-center bg-[rgb(var(--clr-surface))] px-6 text-center">
        <p className="text-body-large text-on-surface-var">
          {t("doctor.profile.public.loading", "Loading doctor profile...")}
        </p>
      </div>
    );
  }

  if (!canEdit && publicProfileUnavailable) {
    return (
      <div className="profile-page-shell flex min-h-dvh items-center justify-center bg-[rgb(var(--clr-surface))] px-6 text-center">
        <div className="max-w-[360px]">
          <h1 className="text-title-large text-on-surface">
            {t("doctor.profile.public.unavailableTitle", "Doctor profile unavailable")}
          </h1>
          <p className="mt-2 text-body-medium text-on-surface-var">
            {t(
              "doctor.profile.public.unavailableBody",
              "This clinician profile is not ready for public viewing yet.",
            )}
          </p>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="mt-5 inline-flex h-11 items-center justify-center rounded-full bg-[rgb(var(--afrimed-primary))] px-5 text-label-large font-semibold text-white"
          >
            {t("common.back", "Back")}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="profile-page-shell w-full">
      {coverError ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto w-full max-w-[420px] rounded-2xl border border-[rgba(var(--schemes-critical),0.25)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 shadow-[0_12px_30px_rgba(4,8,18,0.28)]">
            <p className="text-body-medium text-center text-[rgb(var(--schemes-critical))]">
              {coverError}
            </p>
          </div>
        </div>
      ) : null}

      {/* Cover + overlay controls + avatar */}
      <div className="relative profile-cover-bleed -mt-[16px] md:-mt-[18px]">
        <CoverPhoto src={coverUrl || devCover || COVER_IMAGE} />

        <div className="absolute inset-x-0 top-0">
          <div className="afrimed-rail afrimed-rail--comfortable pt-2">
            <div className="px-[var(--rail-pad)]">
              <TopBarProfile
                variant="doctor"
                onBack={canEdit ? () => nav("/settings/doctor/p/edit-intro") : () => nav(-1)}
                onAttach={canEdit ? openCoverPicker : undefined}
                onCalendar={canEdit ? () => nav("/doctors/appointments") : undefined}
                onRightAction={canEdit ? () => nav("/settings/doctor") : undefined}
                className="mt-16 md:mt-20 lg:mt-24"
              />
            </div>
          </div>
        </div>

      <AvatarHeaderProfile src={photoUrl} />
      </div>

      {/* Content shell */}
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        {canEdit ? (
          <div className="relative min-h-[40px]">
            <div
              ref={shareMenuRef}
              className="relative z-20 flex justify-end md:absolute md:right-0 md:top-7"
            >
              <button
                type="button"
                className="inline-flex min-h-10 items-center gap-2 rounded-[12px] py-1 pl-2 pr-0 text-label-large text-primary transition-colors hover:bg-[rgba(var(--afrimed-primary),0.05)]"
                aria-haspopup="menu"
                aria-expanded={shareMenuOpen}
                onClick={() => setShareMenuOpen((open) => !open)}
              >
                <img src={ICON_SHARE_PROFILE} alt="" className="h-[18px] w-[18px]" draggable="false" />
                <span className="inline">
                  {t("doctor.profile.share.label", "Share profile")}
                </span>
              </button>

              {shareMenuOpen ? (
                <div
                  role="menu"
                  className="absolute right-0 top-[calc(100%+8px)] z-30 min-w-[188px] overflow-hidden rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] py-2 shadow-[0_16px_40px_rgba(15,23,42,0.16)] ring-1 ring-[rgba(24,50,119,0.08)]"
                >
                  {socialShareOptions.map((option) => (
                    <a
                      key={option.key}
                      role="menuitem"
                      href={option.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2.5 text-body-large text-on-surface transition-colors hover:bg-[rgba(var(--afrimed-primary),0.055)]"
                      onClick={() => setShareMenuOpen(false)}
                    >
                      <img src={option.icon} alt="" className="h-6 w-6" draggable="false" />
                      <span>{option.label}</span>
                    </a>
                  ))}
                  <div className="my-1 h-px bg-[rgba(24,50,119,0.08)]" />
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center px-4 py-2.5 text-left text-body-large text-on-surface transition-colors hover:bg-[rgba(var(--afrimed-primary),0.055)]"
                    onClick={handleCopyShareCaption}
                  >
                    {t("doctor.profile.share.copyInvitation", "Copy invitation")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center px-4 py-2.5 text-left text-body-large text-on-surface transition-colors hover:bg-[rgba(var(--afrimed-primary),0.055)]"
                    onClick={handleCopyShareLink}
                  >
                    {t("doctor.profile.share.copyLink", "Copy profile link")}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="min-w-0 overflow-visible py-1 md:ml-[220px] md:mr-[180px]">
              <FolaOnboardingCard role="doctor" placement="doctorCover" />
            </div>
          </div>
        ) : null}

        {/* Name + specialty */}
        <div className={canEdit ? "mt-2 md:mt-14 flex items-start justify-between" : "mt-12 sm:mt-14 md:mt-24 flex items-start justify-between"}>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-title-large text-on-surface">{publicDoctorName}</h1>
              <img
                src={verificationBadgeSrc}
                alt={verificationBadgeAlt}
                className="h-4 w-4"
                draggable="false"
              />
            </div>
            <p className="mt-1 text-body-large text-on-surface-var">
              {localizeDisplayLabel(doctor.specialty, languageCode, "specialty")}
            </p>
          </div>

          {canEdit && (
          <button
            type="button"
            className="mt-1 text-label-large text-primary"
            onClick={() => nav("/settings/doctor/p/edit-name")}
          >
            {editLabel}
          </button>
        )}
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-2.5 sm:gap-3">
          <StatChip
            icon={ICON_PATIENTS}
            label={t("doctor.profile.stats.patients", "Patients")}
            value={doctor.patients}
          />
          <StatChip
            icon={ICON_EXPERIENCE}
            label={t("doctor.profile.stats.experience", "Experience")}
            value={doctor.experienceDisplay}
          />
          <StatChip
            icon={ICON_STAR}
            label={t("doctor.profile.stats.rating", "Rating")}
            value={doctor.ratingDisplay}
            largeIcon
          />
        </div>

        {/* About */}
        <div className="mt-8">
          <SectionHeader
            title={aboutSectionTitle}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? () => nav("/settings/doctor/p/about") : undefined}
          />
          <Card className="p-4 text-body-medium text-on-surface">{doctor.about}</Card>
        </div>

        {/* Focus */}
        <div className="mt-8">
          <SectionHeader
            title={focusSectionTitle}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? () => nav("/settings/doctor/p/focus") : undefined}
          />
          <div className="flex flex-wrap gap-2">
            {focusChips.map((label) => (
              <FocusChip key={label} label={label} />
            ))}
            {focusChips.length === 0 && (
              <div className="text-body-medium text-on-surface-var">
                {canEdit
                  ? t("doctor.profile.focus.addFromSettings", "Add your focus areas from settings.")
                  : t("doctor.profile.focus.empty", "No focus areas added yet.")}
              </div>
            )}
          </div>
        </div>

        {!canEdit &&
        profilePracticeMode !== "virtual_only" &&
        (patientInsuranceDisplay.hasActual || patientInsuranceDisplay.generic) ? (
          <>
            <SectionDivider />
            <div>
              <SectionHeader
                title={t("appointments.doctor.insuranceAccepted", "Insurance accepted")}
              />
              {patientInsuranceDisplay.hasActual ? (
                <div className="flex flex-wrap gap-2">
                  {patientInsuranceDisplay.companies.map((company) => (
                    <FocusChip key={company} label={company} />
                  ))}
                </div>
              ) : (
                <div className="text-body-medium text-on-surface-var">
                  {t(
                    "appointments.doctor.insuranceGeneric",
                    "Accepting insurance — confirm provider during booking."
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}

        <div className="flex flex-col">
        <div style={{ order: getProfileSectionOrder("languages") }}>
        <SectionDivider />

        {/* Languages */}
        <div>
          <SectionHeader
            title={t("patient.profile.sections.languages", isFrenchLanguageCode(languageCode) ? "Langues" : "Languages")}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? () => nav("/settings/doctor/p/languages") : undefined}
          />
          <Card className="mt-2 overflow-hidden">
            <div className="px-4 py-3">
              {preferredDoctorLanguage ? (
                <div>
                  <div className="text-label-medium text-on-surface-var">
                    {t("settings.subflows.languages.preferredLabel", isFrenchLanguageCode(languageCode) ? "Langue préférée / principale" : "Preferred / primary language")}
                  </div>
                  <div className="mt-1 text-body-large text-on-surface">
                    {preferredDoctorLanguage}
                  </div>
                  {otherDoctorLanguages.length > 0 ? (
                    <div className="mt-3">
                      <div className="text-label-medium text-on-surface-var">
                        {t("patient.profile.languages.otherLabel", isFrenchLanguageCode(languageCode) ? "Autres langues" : "Other languages")}
                      </div>
                      <div className="mt-1 text-body-large text-on-surface">
                        {otherDoctorLanguages.join(", ")}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-body-medium text-on-surface-var">
                  {t("patient.profile.placeholders.languages", isFrenchLanguageCode(languageCode) ? "Langues préférées" : "Preferred languages")}
                </div>
              )}
            </div>
          </Card>
        </div>
        </div>

        {showConsultanciesSection ? (
        <div style={{ order: getProfileSectionOrder("consultancies") }}>
        <SectionDivider />

        {/* Consultancies */}
        {(() => {
          const base = Array.isArray(doctor.consultancies)
            ? doctor.consultancies
            : [];

          const consultancies = base;

          return (
            <section className="mt-6">
              <SectionHeader
                title={t("doctor.profile.sections.consultancies", "Consultancies")}
                actionLabel={
                  canEdit
                    ? consultancies.length > 0
                      ? editLabel
                      : addLabel
                    : null
                }
                onAction={canEdit ? () => nav("/settings/doctor/p/consultancies/edit") : undefined}
              />

              {consultancies.length === 0 ? (
                <Card className="mt-2 px-4 py-5">
                  <div className="text-body-medium text-on-surface-var">
                    {t("doctor.profile.consultancies.empty", "No consultancies yet.")}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="mt-3 text-label-large text-primary"
                      onClick={() => nav("/settings/doctor/p/consultancies/edit")}
                    >
                      {t("doctor.profile.consultancies.addLocation", "Add consultancy location")}
                    </button>
                  )}
                </Card>
              ) : (
                <div
                  className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory no-scrollbar"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  {consultancies.map((c) => (
                    <Card
                      as="article"
                      key={c.id}
                      className="
                        snap-start
                        shrink-0
                        !w-[95%]
                        max-w-[360px]
                        overflow-hidden
                      "
                    >
                      <header className="flex items-start justify-between px-4 pt-4 pb-2">
                        <div className="flex items-center gap-3">
                          <img
                            src={ICON_HOSPITAL}
                            alt=""
                            className="h-11 w-11"
                          />
                          <div>
                            <div className="text-title-medium text-on-surface">
                              {c.name}
                            </div>
                            {c.address && (
                              <div className="mt-1 text-body-medium text-on-surface-var">
                                {c.address}
                              </div>
                            )}
                          </div>
                        </div>

                        {canEdit ? (
                          <button
                            type="button"
                            className="
                              h-8 w-8
                              rounded-full
                              flex items-center justify-center
                            "
                            onClick={() => setConsultancyActionTarget(c)}
                            style={{ WebkitTapHighlightColor: "transparent" }}
                          >
                            <img
                              src="/icons/doctor/3dots-edit-button.svg"
                              alt={t("doctor.profile.consultancies.options", "Consultancy options")}
                              className="h-10 w-10"
                              draggable="false"
                            />
                          </button>
                        ) : (
                          <div className="h-8 w-8" />
                        )}
                      </header>

                      <CardDivider />

                      <InfoRow56
                        icon={ICON_FAMILY}
                        label={t("doctor.profile.consultancies.labels.patientsAge", "Patients Age")}
                        value={c.patientsAge}
                      />

                      <CardDivider />

                      <InfoRow56
                        icon={ICON_DATE_RANGE}
                        label={t("doctor.profile.consultancies.labels.availability", isFrenchLanguageCode(languageCode) ? "Disponibilité" : "Availability")}
                        value={c.availability}
                      />

                      <CardDivider />

                      <InfoRow56
                        icon={ICON_INSURANCE}
                        label={t("doctor.profile.consultancies.labels.insurancePolicy", "Insurance Policy")}
                        value={c.insurancePolicy}
                      />

                      <CardDivider />

                      <InfoRow56
                        icon={ICON_PAYMENT_TERMS}
                        label={t("doctor.profile.consultancies.labels.paymentTerms", "Payment Terms")}
                        value={c.paymentTerms}
                      />
                    </Card>
                  ))}
                </div>
              )}
            </section>
          );
        })()}
        </div>
        ) : null}

{showEVisitSection ? (
<div style={{ order: getProfileSectionOrder("eVisit") }}>
<SectionDivider />

{/* Virtual consultations */}
        <div>
          <SectionHeader
            title={t("doctor.profile.sections.virtualConsultations", isFrenchLanguageCode(languageCode) ? "Consultations virtuelles" : "Virtual consultations")}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? handleGoToEVisit : undefined}
          />
          <Card className="mt-2 overflow-hidden">
            <EVisitRow
              icon={ICON_EVISIT}
              label={doctor.eVisit.label}
              status={doctor.eVisit.status}
              onClick={canEdit ? handleGoToEVisit : undefined}
            />

            <CardDivider />

            <InfoRow56
              dense
              icon={ICON_AVAILABILITY}
              label={t("doctor.profile.consultancies.labels.availability", isFrenchLanguageCode(languageCode) ? "Disponibilité" : "Availability")}
              value={doctor.eVisit.availability}
            />

            {doctor.eVisit.priceLabel ? (
              <>
                <CardDivider />
                <InfoRow56
                  dense
                  icon={ICON_PAYMENT_TERMS}
                  label={t("doctor.profile.virtualConsultations.labels.price", "Virtual consultation price")}
                  value={doctor.eVisit.priceLabel}
                />
              </>
            ) : null}
          </Card>
        </div>
        </div>
) : null}

        <div style={{ order: getProfileSectionOrder("gallery") }}>
        <SectionDivider />

{/* Gallery – swipeable 1 + a bit */}
<section className="mt-6">
  <SectionHeader
      title={t("doctor.profile.sections.gallery", "Gallery")}
      actionLabel={galleryActionLabel}
      onAction={galleryActionLabel ? handleGalleryAction : undefined}
    />

  <div
    className="
      flex gap-3 overflow-x-auto no-scrollbar
      pb-2
      snap-x snap-mandatory
    "
    style={{ WebkitOverflowScrolling: "touch" }}
  >
    {galleryImages.map((img) => (
      <div
        key={img.id}
        className="
          snap-start
          shrink-0
          w-[261px] h-[283px]
          rounded-[28px]
          overflow-hidden
          bg-neutral-200
        "
      >
        <img
          src={img.url}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
    ))}

    {/* If no images yet, show a soft placeholder card */}
    {galleryImages.length === 0 && (
      <Card
        className="
          snap-start
          shrink-0
          !w-[184px] h-[184px]
          overflow-hidden
          flex items-center justify-center
          text-body-medium text-on-surface-var
          px-4 text-center
        "
      >
        {canEdit ? (
          <span>
            {t("doctor.profile.gallery.addFromSettingsPrefix", "Add images from your")}{" "}
            <button
              type="button"
              className="text-primary underline-offset-2 hover:underline"
              onClick={() => nav("/settings/doctor/p/gallery")}
            >
              {t("doctor.profile.gallery.gallerySettingsLink", "Gallery settings")}
            </button>
          </span>
        ) : (
          <span>{t("doctor.profile.gallery.emptyPublic", "No gallery images yet.")}</span>
        )}
      </Card>
    )}
  </div>
</section>

        {!canEdit && publicGalleryOpen ? (
          <div className="fixed inset-0 z-[150] bg-[rgba(4,8,18,0.56)] px-4 py-6">
            <div className="mx-auto flex h-full w-full max-w-[1100px] flex-col rounded-[28px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_20px_48px_rgba(4,8,18,0.28)]">
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="text-title-large text-on-surface">
                  {t("doctor.profile.sections.gallery", "Gallery")}
                </div>
                <button
                  type="button"
                  onClick={() => setPublicGalleryOpen(false)}
                  className="h-11 w-11 rounded-[14px] bg-[rgba(24,50,119,0.08)] text-title-large text-on-surface"
                  aria-label={t("common.close", "Close gallery")}
                >
                  ×
                </button>
              </div>

              <div className="px-5 pb-5 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                  {galleryImages.map((img) => (
                    <div
                      key={`public-gallery-${img.id}`}
                      className="overflow-hidden rounded-[24px] bg-[rgba(24,50,119,0.06)] aspect-[0.92]"
                    >
                      <img
                        src={img.url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        </div>

        <div style={{ order: getProfileSectionOrder("services") }}>
        <SectionDivider />

        {/* Services */}
        <div>
          <SectionHeader
              title={servicesSectionTitle}
              actionLabel={canEdit ? editLabel : null}
              onAction={canEdit ? () => nav("/settings/doctor/p/services") : undefined}
            />
          <Card className="mt-2 overflow-hidden">

          {/* Cover picker sheet */}
          {canEdit && (
            <Sheet open={coverPickerOpen} onClose={closeCoverPicker}>
              <div className="mx-auto w-full max-w-[412px]">
                <div className="mt-2 mb-4 text-center">
                  <p className="text-title-large text-on-surface text-center">
                    {t("doctor.profile.cover.editTitle", "Edit cover photo")}
                  </p>
                </div>

                <div className="rounded-[16px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
                  <button
                    type="button"
                    onClick={handleAvatarUploadClick}
                    disabled={coverUploading}
                    className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60 hover:bg-[rgba(24,50,119,0.03)]"
                  >
                    <img src={ICON_UPLOAD_IMAGE} alt="" className="h-5 w-5 ml-2" />
                    <span className="flex-1 text-left text-body-large text-on-surface">
                      {t("doctor.profile.cover.uploadImage", "Upload image")}
                    </span>
                    <span className="mr-2 text-body-medium text-on-surface-var">›</span>
                  </button>

                  <div className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />

                  <button
                    type="button"
                    onClick={handleAvatarTakePhotoClick}
                    disabled={coverUploading}
                    className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60 hover:bg-[rgba(24,50,119,0.03)]"
                  >
                    <img src={ICON_TAKE_PHOTO} alt="" className="h-5 w-5 ml-2" />
                    <span className="flex-1 text-left text-body-large text-on-surface">
                      {t("doctor.profile.cover.takePhoto", "Take photo")}
                    </span>
                    <span className="mr-2 text-body-medium text-on-surface-var">›</span>
                  </button>

                  {hasCustomCover ? (
                    <>
                      <div className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />
                      <button
                        type="button"
                        onClick={requestCoverRemoval}
                        disabled={coverUploading}
                        className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60 hover:bg-[rgba(248,83,58,0.08)]"
                      >
                        <img src={ICON_DELETE_CRITICAL} alt="" className="h-5 w-5 ml-2" />
                        <span className="flex-1 text-left text-body-large text-[rgb(var(--schemes-critical))]">
                          {t("doctor.profile.cover.remove", "Remove cover photo")}
                        </span>
                        <span className="mr-2 text-body-medium text-on-surface-var">›</span>
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </Sheet>
          )}

          {/* Hidden inputs backing the sheet */}
          <input
            ref={coverFileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCoverFileSelected(file);
              e.target.value = "";
            }}
          />

          <input
            ref={coverCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCoverFileSelected(file);
              e.target.value = "";
            }}
          />

          {/* Crop modal */}
          {showCoverCrop && coverSrc && (
            <CoverCropModal
              src={coverSrc}
              onCancel={() => setShowCoverCrop(false)}
              onConfirm={async (blob) => {
              // Always close the crop UI immediately (prevents "Save does nothing")
              setShowCoverCrop(false);
              setCoverError("");

              // BYPASS: no Supabase session -> store locally + update UI
              if (BYPASS) {
                try {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = String(reader.result || "");
                    try {
                      if (devCoverKey) {
                        localStorage.setItem(devCoverKey, dataUrl);
                      }
                    } catch {}
                    setCoverUrl(dataUrl);
                  };
                  reader.readAsDataURL(blob);
                } catch (e) {
                  console.error("[cover] BYPASS failed:", e);
                  setCoverError(
                    t("doctor.profile.cover.errors.save", "We couldn’t save your cover photo. Please try again."),
                  );
                }
                return;
              }

              try {
                setCoverUploading(true);

                const {
                  data: { user },
                  error: authErr,
                } = await supabase.auth.getUser();

                if (authErr) throw authErr;
                if (!user) throw new Error("Not authenticated");

                const filePath = `${user.id}/cover.jpg`;

                const { error: uploadErr } = await supabase.storage
                  .from("cover-images")
                  .upload(filePath, blob, { upsert: true, contentType: "image/jpeg" });

                if (uploadErr) throw uploadErr;

                const { data: signed } = await supabase.storage
                  .from("cover-images")
                  .createSignedUrl(filePath, 60 * 60 * 24);

                const { data: urlData } = supabase.storage
                  .from("cover-images")
                  .getPublicUrl(filePath);

                const newCoverUrl = urlData?.publicUrl || "";

                const { error: dbErr } = await supabase
                  .from("doctor_profiles")
                  .upsert(
                    {
                      user_id: user.id,
                      cover_photo_path: filePath,
                      cover_image_url: newCoverUrl || null,
                    },
                    { onConflict: "user_id" }
                  );

                if (dbErr) throw dbErr;

                // 🔒 UI source of truth
                setCoverUrl(signed?.signedUrl || newCoverUrl || "");
                try {
                  await auth?.refreshProfile?.();
                  localStorage.setItem("afrimed:profile:refresh", String(Date.now()));
                } catch {
                  // non-fatal
                }
              } catch (err) {
                console.error("[cover] failed:", err);
                setCoverError(
                  t("doctor.profile.cover.errors.save", "We couldn’t save your cover photo. Please try again."),
                );
              } finally {
                setCoverUploading(false);
              }
            }}
            />
          )}

            {doctor.services.length === 0 ? (
              <div className="px-4 py-6 text-body-medium text-on-surface-var">
                {t(
                  "doctor.profile.services.empty",
                  "Add services and prices from settings to show patients what you offer.",
                )}
              </div>
            ) : (
              doctor.services.map((service, index) => (
                <React.Fragment key={service.id ?? `${service.name}-${index}`}>
                  <div className="px-4 py-3 flex items-center justify-between gap-3">
                    <span className="text-body-large text-on-surface">
                      {localizeDisplayLabel(service.name, languageCode, "service")}
                    </span>
                    <span className="text-body-large text-on-surface whitespace-nowrap">
                      {service.price}{" "}
                      <span className="text-on-surface-var">
                        {service.currency}
                      </span>
                    </span>
                  </div>
                  {index < doctor.services.length - 1 && <CardDivider />}
                </React.Fragment>
              ))
            )}
          </Card>
        </div>
        </div>
        </div>
      </div>

      {canEdit && consultancyActionTarget && (
        <Sheet
          open={Boolean(consultancyActionTarget)}
          onClose={() => setConsultancyActionTarget(null)}
        >
          <div className="mx-auto w-full max-w-[420px]">
            <p className="mt-2 mb-4 text-title-large text-on-surface text-center">
              {consultancyActionTarget?.name ||
                t("doctor.profile.sections.consultancy", "Consultancy")}
            </p>

            <div className="rounded-[16px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
              <button
                type="button"
                onClick={() => {
                  const id = consultancyActionTarget?.id;
                  setConsultancyActionTarget(null);
                  nav("/settings/doctor/p/consultancies/edit", {
                    state: { consultancyId: id },
                  });
                }}
                className="w-full flex items-center gap-3 px-2 py-3 hover:bg-[rgba(24,50,119,0.03)]"
              >
                <img src="/icons/doctor/edit_square.svg" alt="" className="h-5 w-5 ml-2" />
                <span className="flex-1 text-left text-body-large text-on-surface">
                  {t("doctor.profile.consultancies.edit", "Edit consultancy")}
                </span>
                <span className="mr-2 text-body-medium text-on-surface-var">›</span>
              </button>

              <div className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />

              <button
                type="button"
                onClick={() => setConsultancyDeleteOpen(true)}
                className="w-full flex items-center gap-3 px-2 py-3 hover:bg-[rgba(248,83,58,0.08)]"
              >
                <img src={ICON_DELETE_CRITICAL} alt="" className="h-5 w-5 ml-2" />
                <span className="flex-1 text-left text-body-large text-[rgb(var(--schemes-critical))]">
                  {t("doctor.profile.consultancies.remove", "Remove consultancy")}
                </span>
                <span className="mr-2 text-body-medium text-on-surface-var">›</span>
              </button>
            </div>
          </div>
        </Sheet>
      )}

      <BasicDialogCritical
        open={consultancyDeleteOpen}
        title={t("doctor.profile.consultancies.removeTitle", "Remove this consultancy?")}
        body={t("doctor.profile.consultancies.removeBody", "This consultancy will be removed from your profile.")}
        cancelText={t("common.cancel", isFrenchLanguageCode(languageCode) ? "Annuler" : "Cancel")}
        okText={
          consultancyDeleting
            ? t("doctor.profile.consultancies.removing", "Removing...")
            : t("doctor.profile.consultancies.remove", "Remove")
        }
        onCancel={() => setConsultancyDeleteOpen(false)}
        onOk={handleDeleteConsultancy}
        disableBackdropClose={consultancyDeleting}
      />

      <BasicDialogCritical
        open={coverDeleteOpen}
        title={t("doctor.profile.cover.removeTitle", "Remove your cover photo?")}
        body={t("doctor.profile.cover.removeBody", "Your profile will use the AfriMed™ default cover instead.")}
        cancelText={t("common.cancel", isFrenchLanguageCode(languageCode) ? "Annuler" : "Cancel")}
        okText={
          coverUploading
            ? t("doctor.profile.cover.removing", "Removing...")
            : t("doctor.profile.cover.remove", "Remove")
        }
        onCancel={() => setCoverDeleteOpen(false)}
        onOk={handleRemoveCover}
        disableBackdropClose={coverUploading}
      />
    </div>
  );
}
